function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { PageHeader } from "../../components/analytics-kit";
import { RectCanvas } from "./edit-mode";
import * as api from "./images-api";
import { editorBack } from "./clip-editor";
import { NewTop, Segs, Chips, Sect, PlaceholderTiles, RefPicker } from "./image-new";
import { ShopifyPushDialog, ShopifyBlock, ColorwaySheet, BrushCanvas, renderMaskPng, BRUSH_SIZES, LanguageChips, FontsSection, SimilarStrip, StatsStrip, DupNotice } from "./image-workspace-phase2";

/*
 * Commerce image workspace, Phase 1 (PlunkDocs specs/IMAGE_WORKSPACE.md, mockup image-workspace-2026-10-05.html).
 * Routes: /Content/images/new (first run, create start state) and /Content/images/:id (the workspace).
 *
 * One screen: history rail · canvas · placements rail · side panel (six tiles: Background, Extend, Retouch, Recolor,
 * Straighten, Text) · Approve and Export sheet. Every operation is a new version; the original is never touched.
 * DS decisions (§24): previews free and unpriced, NO cost amounts anywhere, the only credit message is the
 * insufficient-credits state with "Buy credits", approval gating off by default (approvers = any admin or owner),
 * people OFF by default behind the tenant switch, no daily caps, no disclaimers, prompt-create without an anchor is
 * allowed with the "Unverified" badge, polling only, "Open in Studio" only when the tenant has both apps.
 * Phase 2 (image-workspace-phase2.jsx): Push to Shopify, colorway sets, text per language with Translate, the fonts
 * registry, brush masks for Retouch, the near-duplicate finder, similar images and per-image stats.
 */

const LANGS = [["en", "EN"], ["es", "ES"], ["fr", "FR"]];
// Look chips on New image (2026-10-08): the API adds one style line to the prompt (ImagesController.LookLine).
const LOOKS = [["photo", "Photo"], ["studio", "Studio"], ["lifestyle", "Lifestyle"], ["illustration", "Illustration"]];
const SHAPES = [["1:1", "Square"], ["4:5", "Portrait"], ["16:9", "Wide"], ["9:16", "Story"]];
const COUNTS = [[1, "1"], [2, "2"], [4, "4"]];
const EXTEND_ASPECTS = [["1:1", "Square 1:1"], ["4:5", "Portrait 4:5"], ["9:16", "Story 9:16"], ["1.91:1", "Wide 1.91:1"], ["2:1", "Email 2:1"], ["12:5", "Site 12:5"]];
const PEOPLE_KEYS = new Set(["on_model", "in_hand", "in_use", "in_play"]);
const RIGHTS = [["own", "Our own photo", "Shot by us or for us"], ["licensed", "Licensed", "Stock or a licensed shoot"], ["brand_supplied", "Brand supplied", "From the manufacturer or brand"]];
const COLORS = [["white_on_dark", "White on dark"], ["brand", "Brand accent"], ["ink", "Ink"]];
const TILES = [["background", "layers_clear", "Background"], ["retouch", "auto_fix_high", "Change area"], ["straighten", "straighten", "Straighten"], ["crop", "crop", "Crop"], ["flip", "flip", "Flip"], ["rotate", "rotate_right", "Rotate"], ["extend", "open_in_full", "Extend"], ["recolor", "palette", "Colors"], ["packshot", "center_focus_strong", "Packshot"], ["upscale", "hd", "Upscale"], ["text", "title", "Text"], ["logo", "branding_watermark", "Logo"], ["badge", "sell", "Badge"]];
const TILE_GROUPS = [["Fix", ["background", "retouch", "straighten", "crop", "flip", "rotate"]], ["Create", ["extend", "recolor", "packshot", "upscale", "text", "logo"]]];
/** Side panel header per tool: a plain name and one sentence on what it does. Works for any image, not one product type. */
const TOOL_INFO = {
  background: ["Background", "Swap the background for white, transparent, your brand color or a scene. The product never moves."],
  retouch: ["Change one area", "Mark an area on the image, then describe the change in the bar under it. Everything outside stays exactly as it is."],
  straighten: ["Straighten", "Level a tilted photo."],
  crop: ["Crop", "Draw a box on the image. What is inside becomes the new version."],
  flip: ["Flip", "Mirror the image. Text and logos in the photo mirror too."],
  rotate: ["Rotate", "Quarter turns. Use Straighten for small angles."],
  extend: ["Extend", "Grow the canvas to a new shape. The image continues naturally at the edges."],
  recolor: ["Colors", "Show the product in its other colors or materials. Its shape and everything around it stay the same."],
  packshot: ["Packshot", "Cut the product out and center it on a clean background."],
  upscale: ["Upscale", "Double the pixel size for sharp zoom on product pages."],
  text: ["Text", "Headline, sub line and call to action, drawn live and added at export."],
  logo: ["Logo", "Your brand kit logo, placed with a safe margin."],
  badge: ["Badge", "A short label like Sale or New, burned into the image."]
};
const APPROVAL_STEPS = [["draft", "Draft"], ["in_review", "In review"], ["approved", "Approved"], ["published", "Published"]];
const NO_CAPTION = new Set(["shopify_pdp", "site_hero", "instagram_story"]);
const idStr = v => v === undefined || v === null ? "" : String(v);
const nice = k => String(k || "").replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
const toast = api.toast;
function timeAgo(d) {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return t.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}
function userShort(u) {
  return String(u || "").split("@")[0] || "";
}
function sourceLabel(s) {
  const m = {
    shopify: "Shopify",
    edit: "Edited",
    plunk_image: "Generated",
    upload: "Upload",
    catalog: "Catalog",
    feed: "Feed",
    studio: "Studio"
  };
  return m[s] || (s ? nice(s) : "");
}
function approvalLabel(s) {
  const m = {
    draft: "Draft",
    in_review: "In review",
    approved: "Approved",
    published: "Published"
  };
  return m[s] || "Draft";
}
function approvalTone(s) {
  return s === "approved" ? "ok" : s === "published" ? "info" : s === "in_review" ? "warn" : "";
}
// short = label only (the note is shown beside it, e.g. in the Edit tab's check card).
function fidelityChip(f, aiGenerated, short) {
  if (!f || !f.state) return aiGenerated ? /*#__PURE__*/React.createElement("span", {
    className: "plk-chip"
  }, "Unverified") : null;
  const st = String(f.state).toLowerCase();
  const note = !short && f.note ? `: ${f.note}` : "";
  if (st === "checked" || st === "pass") return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip ok",
    title: f.note || ""
  }, "Checked", note);
  if (st === "failed" || st === "fail" || st === "warn") return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip warn",
    title: f.note || ""
  }, "Needs a look", note);
  return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip",
    title: f.note || ""
  }, "Unverified");
}
function opLabel(v) {
  if (!v) return "";
  if (!v.parentVersionId && Number(v.version || 1) <= 1) return sourceLabel(v.source) || "Original";
  const op = v.op || {};
  const a = String(op.action || v.action || "").toLowerCase();
  const p = op.params || {};
  if (a === "background") return `Background · ${String(p.mode || "").replace(/^scene:/, "").replace(/_/g, " ") || "studio"}`;
  if (a === "extend") return `Extend · ${p.aspect || p.placementKey || ""}`.trim();
  if (a === "retouch") return p.erase ? "Erase" : "Retouch";
  if (a === "recolor") return `Recolor · ${p.target || ""}`.trim();
  if (a === "straighten") return `Straighten · ${p.degrees != null ? p.degrees + "°" : ""}`.trim();
  if (a === "text") return "Text layer";
  if (a === "create") return "Generated";
  if (a === "hires") return "Hi-res";
  return a ? nice(a) : "Version";
}
function normVersion(v, i) {
  const out = {
    ...v
  };
  out.id = idStr(v.id);
  out.parentVersionId = v.parentVersionId ? idStr(v.parentVersionId) : v.parentAssetId && i > 0 ? idStr(v.parentAssetId) : null;
  out.version = Number(v.version || i + 1);
  out.url = v.url || v.latestUrl || "";
  out.width = Number(v.width) || 0;
  out.height = Number(v.height) || 0;
  out.textLayers = Array.isArray(v.textLayers) ? v.textLayers : v.textLayers && Array.isArray(v.textLayers.layers) ? v.textLayers.layers : [];
  out.placements = Array.isArray(v.placements) ? v.placements.map(String) : [];
  out.fidelity = v.fidelity || null;
  if (out.fidelity && out.fidelity.verdict && !out.fidelity.state) out.fidelity.state = out.fidelity.verdict === "pass" ? "checked" : out.fidelity.verdict === "fail" ? "failed" : "warn";
  if (v.productBox && typeof v.productBox === "string") {
    try {
      out.productBox = JSON.parse(v.productBox);
    } catch (e) {
      out.productBox = null;
    }
  }
  return out;
}
/** A create output → {id, url, width, height, title} from GET /images/{id} (the version with that id, else the newest). */
function previewOf(id, d) {
  const vs = (d && d.versions || []).map(normVersion);
  const v = vs.find(x => x.id === String(id)) || vs[vs.length - 1] || (d && d.root ? normVersion(d.root, 0) : null);
  return {
    id: String(id),
    url: v && v.url || "",
    width: v && v.width || 0,
    height: v && v.height || 0,
    title: v && v.title || ""
  };
}
function layersToMap(layers) {
  const m = {
    headline: null,
    sub: null,
    cta: null
  };
  (layers || []).forEach(l => {
    if (l && (l.kind || l.role) && m[l.kind || l.role] === null) m[l.kind || l.role] = l;
  });
  return m;
}
function textOf(layer, lang, base) {
  if (!layer) return "";
  const t = layer.text || {};
  return t[lang] || t[base || "en"] || t.en || "";
}
/** Every language present on a set of layers. */
function layerLangs(layers) {
  const out = [];
  (layers || []).forEach(l => {
    Object.keys(l && l.text || {}).forEach(k => {
      if (k && out.indexOf(k) === -1) out.push(k);
    });
  });
  return out;
}
function normLayers(d) {
  const arr = Array.isArray(d) ? d : d && Array.isArray(d.layers) ? d.layers : d && d.textLayers ? Array.isArray(d.textLayers) ? d.textLayers : d.textLayers.layers || [] : [];
  return arr.filter(Boolean);
}

/* ---------- small shared pieces ---------- */
const Seg = ({
  list,
  value,
  onChange,
  disabled,
  off,
  multi
}) => /*#__PURE__*/React.createElement("span", {
  className: "iw-seg"
}, list.map(([k, l, title]) => {
  const on = multi ? (value || []).indexOf(k) !== -1 : value === k;
  const isOff = off && off(k);
  return /*#__PURE__*/React.createElement("button", {
    key: String(k),
    type: "button",
    className: "territory-toggle xs" + (on ? multi ? " on" : " active" : "") + (isOff ? " off" : ""),
    disabled: disabled || isOff,
    title: isOff ? title || "People are off for this workspace" : title || "",
    onClick: () => onChange(k)
  }, l);
}));
const Row = ({
  k,
  children,
  hint
}) => /*#__PURE__*/React.createElement("div", {
  className: "iw-row"
}, /*#__PURE__*/React.createElement("span", {
  className: "k"
}, k), children, hint ? /*#__PURE__*/React.createElement("span", {
  className: "territory-hint"
}, hint) : null);
const Check = ({
  checked,
  onChange,
  children,
  disabled
}) => /*#__PURE__*/React.createElement("label", {
  className: "ec-check iw-check"
}, /*#__PURE__*/React.createElement("input", {
  type: "checkbox",
  checked: !!checked,
  disabled: disabled,
  onChange: e => onChange(e.target.checked)
}), /*#__PURE__*/React.createElement("span", null, children));

/** Text overlays drawn live by the App (never into the pixels) on the canvas and in every crop. */
function TextOverlay({
  layers,
  lang,
  base,
  scale = 1,
  ghost
}) {
  const m = layersToMap(layers);
  const h = textOf(m.headline, lang, base),
    s = textOf(m.sub, lang, base),
    c = textOf(m.cta, lang, base);
  if (!h && !s && !c) return null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, h ? /*#__PURE__*/React.createElement("div", {
    className: "iw-ovl" + (ghost ? " ghost" : ""),
    style: {
      fontSize: 13 * scale
    }
  }, h) : null, s ? /*#__PURE__*/React.createElement("div", {
    className: "iw-ovl sub" + (ghost ? " ghost" : ""),
    style: {
      fontSize: 10.5 * scale,
      top: h ? `calc(8% + ${26 * scale}px)` : "8%"
    }
  }, s) : null, c ? /*#__PURE__*/React.createElement("div", {
    className: "iw-ovl cta" + (ghost ? " ghost" : ""),
    style: {
      fontSize: 10 * scale
    }
  }, c) : null);
}

/** One placement crop with its safe zone (data from the registry), the fit chip and the include tick. */
/** One channel's crop of a version: the image as the channel frames it, its safe zone (dashed) and the areas the channel
 *  covers with its own UI (hatched). Drawn small in the placements grid and large in the canvas when previewed. */
function PlacementFrame({
  p,
  version,
  lang,
  base,
  scale,
  style
}) {
  const {
    inset,
    clear
  } = api.safeZone(p);
  return /*#__PURE__*/React.createElement("div", {
    className: "fr",
    style: {
      aspectRatio: `${api.aspectNum(p.aspect)}`,
      ...(style || {})
    }
  }, version.url ? /*#__PURE__*/React.createElement("img", {
    src: version.url,
    alt: ""
  }) : null, clear.map((c, i) => {
    const st = {};
    const pct = `${c.pct * 100}%`;
    if (c.side === "top") Object.assign(st, {
      left: 0,
      right: 0,
      top: 0,
      height: pct
    });else if (c.side === "bottom") Object.assign(st, {
      left: 0,
      right: 0,
      bottom: 0,
      height: pct
    });else if (c.side === "left") Object.assign(st, {
      left: 0,
      top: 0,
      bottom: 0,
      width: pct
    });else if (c.side === "right") Object.assign(st, {
      right: 0,
      top: 0,
      bottom: 0,
      width: pct
    });else if (c.side === "bottom-right") Object.assign(st, {
      right: 0,
      bottom: 0,
      width: pct,
      height: pct
    });else if (c.w && c.h) Object.assign(st, {
      left: `${c.x * 100}%`,
      top: `${c.y * 100}%`,
      width: `${c.w * 100}%`,
      height: `${c.h * 100}%`
    });
    return /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "clear",
      style: st
    });
  }), /*#__PURE__*/React.createElement("div", {
    className: "safe",
    style: {
      top: `${inset.t * 100}%`,
      right: `${inset.r * 100}%`,
      bottom: `${inset.b * 100}%`,
      left: `${inset.l * 100}%`
    }
  }), /*#__PURE__*/React.createElement(TextOverlay, {
    layers: version.textLayers,
    lang: lang,
    base: base,
    scale: scale,
    ghost: !p.textAllowed
  }));
}

/** Placement card (2026-10-08): click the crop to see it large in the canvas; the checkbox under it only says whether
 *  Approve and Export makes this size. */
function PlacementCard({
  p,
  version,
  on,
  previewing,
  lang,
  base,
  onToggle,
  onExtend,
  onPreview,
  busy
}) {
  const fit = api.placementFit(version, p);
  const hasText = (version.textLayers || []).some(l => l && l.text && (l.text[base || "en"] || l.text[lang]));
  const wide = api.aspectNum(p.aspect) >= 1; // every frame sits in the same-height stage so names, sizes and badges line up across the grid
  return /*#__PURE__*/React.createElement("div", {
    className: "iw-plc" + (previewing ? " sel" : "")
  }, /*#__PURE__*/React.createElement("b", {
    className: "nm",
    title: p.name
  }, p.name), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "stage",
    title: `See the ${p.name} crop large`,
    onClick: onPreview
  }, /*#__PURE__*/React.createElement(PlacementFrame, {
    p: p,
    version: version,
    lang: lang,
    base: base,
    scale: 0.55,
    style: {
      width: wide ? "100%" : "auto",
      height: wide ? "auto" : "100%"
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "lab"
  }, /*#__PURE__*/React.createElement("span", null, p.width, "x", p.height, " \xB7 ", p.format || "jpg", p.textAllowed === false ? " · no text in image" : "")), !fit.fits ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "iw-need",
    disabled: busy,
    title: `Extend v${version.version} to ${p.aspect} so the product sits inside the safe zone`,
    onClick: onExtend
  }, "needs extend \xB7 1 click") : !p.textAllowed && hasText ? /*#__PURE__*/React.createElement("span", {
    className: "iw-ok"
  }, "text omitted on this channel") : /*#__PURE__*/React.createElement("span", {
    className: "iw-ok"
  }, "fits"), /*#__PURE__*/React.createElement(Check, {
    checked: on,
    onChange: onToggle,
    disabled: busy
  }, "Export this size"));
}

/** Why a version has no fidelity verdict (the check runs by itself on every AI edit, against the product). */
function noCheckReason(v) {
  if (!v || !v.parentVersionId) return "The original. AI edits are checked against it.";
  if (!v.aiGenerated) return "Not needed. No AI change (crop, flip, rotate, text).";
  return "Runs by itself on AI edits. No result yet for this version.";
}

/* =================================================================== the view */
class ImageWorkspace extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "_back", editorBack());
    _defineProperty(this, "state", {
      // shared
      placements: null,
      recipes: null,
      settings: null,
      registryMissing: false,
      // first run + create
      mode: "create",
      pickQ: "",
      pickList: null,
      pickProducts: null,
      upRights: "own",
      upNote: "",
      upBusy: "",
      upErr: "",
      productQ: "",
      products: null,
      anchor: null,
      anchorRecipe: null,
      prompt: "",
      shot: "",
      scene: "",
      look: "",
      refs: [],
      refList: null,
      shape: "1:1",
      count: 4,
      language: "en",
      picks: [],
      createBusy: "",
      createErr: "",
      // new-image screens (2026-10-08): previews stay on the page until one is opened
      previews: [],
      selId: null,
      pendingN: 0,
      moreOpts: false,
      refPicker: false,
      refSearch: null,
      upDrag: false,
      upPct: null,
      // workspace
      family: undefined,
      loadErr: "",
      currentId: null,
      tool: "",
      rect: null,
      busy: "",
      compare: false,
      lang: "en",
      saved: "",
      pendingJobs: 0,
      bgMode: "white",
      bgHex: "#ffffff",
      bgScene: "",
      exAspect: "9:16",
      exHint: "",
      rtPrompt: "",
      rtErase: false,
      rcAttr: "",
      rcValue: "",
      rcFree: "",
      stDeg: 0,
      stCrop: true,
      flipH: true,
      rotDeg: 90,
      psBg: "white",
      psHex: "#ffffff",
      psShadow: true,
      psPad: 8,
      lgPos: "bottom_right",
      lgScale: 18,
      bdText: "Sale",
      bdPos: "top_left",
      text: {
        headline: {
          en: ""
        },
        sub: {
          en: ""
        },
        cta: {
          en: ""
        }
      },
      font: "brand",
      fontId: null,
      color: "white_on_dark",
      product: null,
      options: {},
      approveOpen: false,
      trashConfirm: false,
      // phase 2
      shopify: null,
      similar: null,
      stats: null,
      fonts: undefined,
      addedLangs: [],
      textBusy: "",
      maskMode: "box",
      strokes: [],
      brushSize: 0.04,
      natural: null,
      colorwayOpen: false,
      upSimilar: null,
      upPending: null,
      // redesign (2026-10-08): where the prompt applies, which side tab is open, the More menu, picked colors
      area: "whole",
      sideTab: "edit",
      moreOpen: false,
      rcPicks: [],
      colorwayInit: null
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "_timers", {});
    _defineProperty(this, "alive", () => this._alive);
    _defineProperty(this, "boot", async () => {
      const id = this.imageId();
      this.setState({
        family: undefined,
        loadErr: "",
        currentId: null,
        previewKey: null,
        area: "whole",
        rcPicks: [],
        moreOpen: false,
        tool: "",
        rect: null,
        strokes: [],
        compare: false,
        approveOpen: false,
        colorwayOpen: false,
        trashConfirm: false,
        mode: this.queryMode(),
        prompt: this.query().prompt || this.state.prompt,
        pickList: null,
        shopify: null,
        similar: null,
        stats: null,
        addedLangs: [],
        upSimilar: null,
        upPending: null
      });
      if (!this.state.placements) {
        const [reg, cats, settings, fonts] = await Promise.all([api.getPlacements(), api.getCategories(), api.getSettings(), api.getFonts().catch(() => null)]);
        if (!this._alive) return;
        this.setState({
          placements: reg && reg.length ? reg : api.FALLBACK_PLACEMENTS,
          registryMissing: !(reg && reg.length),
          recipes: (cats && cats.length ? cats : api.FALLBACK_RECIPES).map(api.normRecipe),
          settings: settings || null,
          fonts: fonts || null
        });
      }
      if (id) await this.loadFamily(id);else if (this.query().product) this.pickAnchor({
        id: this.query().product,
        title: this.query().title || ""
      });else if (this.queryMode() === "create") {
        if (!this.state.anchorRecipe) this.clearAnchor();else if (!this.state.refList) api.listLibraryImages("", 24).then(lib => {
          if (this._alive) this.setState({
            refList: lib || []
          });
        });
      } else if (this.queryMode() === "pick" && !this.state.pickList) this.loadPick("");
    });
    /** Switch between the three ways: the URL carries the way so the Create dialog links and Back both work. */
    _defineProperty(this, "goMode", m => {
      if (this.queryMode() !== m) {
        this.props.history.push(`/Content/images/new?mode=${m}`);
        return;
      }
      // Same URL, different screen (a product without a photo moved us to Describe it): switch in place.
      this.setState({
        mode: m
      });
      if (m === "pick" && !this.state.pickList) this.loadPick(this.state.pickQ.trim());
      if (m === "create" && !this.state.anchorRecipe) this.clearAnchor();
    });
    /* ---------- family ---------- */
    _defineProperty(this, "loadFamily", async (id, selectId) => {
      let d = null;
      try {
        d = await api.getImage(id);
      } catch (e) {
        if (!this._alive) return;
        if (api.isSoft(e)) {
          this.setState({
            family: null,
            loadErr: "soft"
          });
          return;
        }
        this.setState({
          family: null,
          loadErr: e && e.message || "Could not open this image."
        });
        return;
      }
      if (!this._alive || !d) return;
      const versions = (d.versions || []).map(normVersion).sort((a, b) => a.version - b.version);
      if (!versions.length && d.root) versions.push(normVersion(d.root, 0));
      const latest = versions[versions.length - 1];
      const keep = selectId || (this.state.currentId && versions.some(v => v.id === this.state.currentId) ? this.state.currentId : null);
      const cur = versions.find(v => v.id === String(keep)) || latest;
      const fam = {
        id: String(id),
        root: d.root ? normVersion(d.root, 0) : versions[0],
        versions,
        product: d.product || null,
        category: d.category ? api.normRecipe(d.category) : null,
        approval: d.approval || {
          required: false,
          canApprove: null
        },
        registry: d.placementsRegistry && d.placementsRegistry.length ? d.placementsRegistry : null
      };
      const options = this.optionsOf(fam.product);
      this.setState({
        family: fam,
        currentId: cur ? cur.id : null,
        placements: fam.registry || this.state.placements,
        saved: cur ? `Saved · v${cur.version}` : "",
        product: fam.product,
        options,
        rcAttr: Object.keys(options)[0] || "",
        rcValue: "",
        lang: cur && cur.language || this.state.lang
      }, () => this.syncText());
      this.loadSide(fam);
      if (fam.product && !Object.keys(options).length) {
        const pc = await api.assetProduct(fam.root ? fam.root.id : id);
        const p = pc && pc.products && pc.products[0];
        if (p && this._alive) {
          const o = this.optionsOf(p);
          this.setState({
            options: o,
            rcAttr: Object.keys(o)[0] || ""
          });
        }
      }
    });
    /** Phase 2 side loads, each fail-soft and independent: Shopify rows, similar images, 30-day stats. Variant options also come from the Shopify product when the catalog has none. */
    _defineProperty(this, "loadSide", fam => {
      const id = fam.id;
      api.getShopify(id).then(sh => {
        if (!this._alive || !sh) return;
        const patch = {
          shopify: sh
        };
        if (!Object.keys(this.state.options || {}).length && sh.product && (sh.product.variants || []).length) {
          const o = {};
          sh.product.variants.forEach(v => (v.options || []).forEach(op => {
            if (!op || !op.name) return;
            o[op.name] = o[op.name] || [];
            if (op.value && o[op.name].indexOf(op.value) === -1) o[op.name].push(op.value);
          }));
          if (Object.keys(o).length) {
            patch.options = o;
            patch.rcAttr = Object.keys(o)[0];
          }
        }
        this.setState(patch);
      }).catch(() => {});
      api.getSimilar(id, 8).then(items => {
        if (this._alive) this.setState({
          similar: items ? items.filter(x => x.id && x.id !== id && x.rootId !== id) : null
        });
      }).catch(() => {});
      api.getStats(id, "30d").then(st => {
        if (this._alive) this.setState({
          stats: st
        });
      }).catch(() => {});
    });
    _defineProperty(this, "select", v => {
      this.setState({
        currentId: v.id,
        rect: null,
        strokes: [],
        compare: false,
        saved: `Saved · v${v.version}`
      }, () => this.syncText());
    });
    _defineProperty(this, "translate", async () => {
      const fam = this.state.family,
        cur = this.current();
      if (!fam || !cur) return;
      const base = this.baseLang(cur);
      const to = this.langs(cur).filter(k => k !== base);
      if (!to.length) return;
      this.setState({
        textBusy: "translate"
      });
      try {
        if (this.state.saved === "Saving…") await this.saveText();
        const r = await api.translateText(fam.id, cur.id, to);
        const layers = normLayers(r && (r.textLayers || r.layers || r));
        if (!layers.length) throw new Error("No translations came back.");
        if (!this._alive) return;
        const versions = fam.versions.map(v => v.id === cur.id ? {
          ...v,
          textLayers: layers
        } : v);
        this.setState({
          family: {
            ...fam,
            versions
          }
        }, () => {
          this.syncText();
          this.debounce("text", this.saveText, 300);
        });
        toast(`Translated into ${to.map(api.langName).join(", ")}.`);
      } catch (e) {
        toast(api.isSoft(e) ? "Translate is not available yet." : e && e.message || "Could not translate the layers.");
      } finally {
        if (this._alive) this.setState({
          textBusy: ""
        });
      }
    });
    /* ---------- jobs ---------- */
    _defineProperty(this, "runJob", async (start, step, successMsg) => {
      const fam = this.state.family;
      if (!fam) return;
      this.setState({
        busy: step,
        pendingJobs: this.state.pendingJobs + 1
      });
      try {
        const r = await start();
        const jobId = r && (r.jobId || r.id);
        if (!jobId) {
          toast("The job did not start.");
          return;
        }
        const done = await api.waitJob(jobId, this.alive);
        if (!this._alive) return;
        if (done.status === "done") {
          await this.loadFamily(fam.id, done.outputVersionId);
          toast(successMsg || "New version ready.");
          this.setState({
            rect: null,
            strokes: []
          });
        } else if (done.status === "timeout") toast("Still working: the version lands in the history rail when it finishes.");else toast(done.error || "The job failed.");
      } catch (e) {
        this.jobError(e, "Could not start the job.");
      } finally {
        if (this._alive) this.setState({
          busy: "",
          pendingJobs: Math.max(0, this.state.pendingJobs - 1)
        });
      }
    });
    _defineProperty(this, "jobError", (e, fallback) => {
      if (!e) {
        toast(fallback);
        return;
      }
      if (api.isSoft(e)) {
        toast("This operation is not available yet.");
        return;
      }
      const code = e.data && (e.data.code || e.data.error);
      if (e.status === 402 || code === "insufficient_credits" || code === "out_of_credits") {
        this.setState({
          credits: {
            insufficient: true,
            buyUrl: e.data && e.data.buyUrl || "https://account.plunk.io/?from=app"
          }
        });
        return;
      }
      if (code === "faces_in_reference") {
        toast("A reference has a face in it: it stays in your library but is never sent to the model.");
        return;
      }
      if (code === "not_a_variant") {
        toast("Recolor targets come from the product's own options.");
        return;
      }
      if (code === "people_off") {
        toast("People are off for this workspace (Manage Data › Content settings).");
        return;
      }
      if (code === "version_locked") {
        toast("A hi-res render is in flight for this version. Try again in a moment.");
        return;
      }
      toast(e.message || fallback);
    });
    _defineProperty(this, "apply", async forced => {
      const cur = this.current();
      if (!cur) return;
      const tool = typeof forced === "string" && forced || this.state.tool;
      const {
        bgMode,
        bgHex,
        bgScene,
        exAspect,
        exHint,
        rtPrompt,
        rtErase,
        rect,
        rcAttr,
        rcValue,
        rcFree,
        stDeg,
        stCrop,
        options,
        maskMode,
        strokes,
        natural
      } = this.state;
      let action = tool,
        params = {};
      if (tool === "background") params = {
        mode: bgMode === "scene" ? `scene:${bgScene || ((this.state.family.category || {}).scenes || [])[0] || "studio"}` : bgMode === "hex" ? bgHex : bgMode
      };
      if (tool === "extend") params = {
        aspect: exAspect,
        hint: exHint.trim() || undefined
      };
      if (tool === "retouch") params = rtErase ? {
        erase: true
      } : {
        prompt: rtPrompt.trim()
      };
      if (tool === "recolor") params = Object.keys(options).length ? {
        attribute: rcAttr,
        target: this.state.rcPicks[0] || rcValue
      } : {
        attribute: "Color",
        target: this.freeColors()[0] || rcFree.trim()
      };
      if (tool === "straighten") params = {
        degrees: Number(stDeg) || 0,
        autoCrop: !!stCrop
      };
      if (tool === "crop") params = {
        rect
      };
      if (tool === "flip") params = {
        horizontal: !!this.state.flipH
      };
      if (tool === "rotate") params = {
        degrees: Number(this.state.rotDeg) || 90
      };
      if (tool === "upscale") params = {
        factor: 2
      };
      if (tool === "packshot") params = {
        background: this.state.psBg === "hex" ? this.state.psHex : this.state.psBg,
        padding: (Number(this.state.psPad) || 8) / 100,
        shadow: !!this.state.psShadow
      };
      if (tool === "logo") params = {
        position: this.state.lgPos,
        scale: (Number(this.state.lgScale) || 18) / 100
      };
      if (tool === "badge") params = {
        text: this.state.bdText.trim(),
        position: this.state.bdPos
      };
      const body = {
        versionId: cur.id,
        action,
        params
      };
      if (tool === "retouch" && this.state.area === "brush" && strokes.length) {
        const w = cur.width || natural && natural.w,
          h = cur.height || natural && natural.h;
        if (!w || !h) {
          toast("The image size is not known yet. Try again in a moment.");
          return;
        }
        this.setState({
          busy: "Uploading the mask…"
        });
        try {
          const png = await renderMaskPng(strokes, w, h);
          body.maskKey = await api.uploadMask(png, `mask-${cur.id}.png`);
        } catch (e) {
          if (this._alive) {
            this.setState({
              busy: ""
            });
            toast(e && e.message || "Could not upload the mask.");
          }
          return;
        }
        if (!this._alive) return;
      } else if (tool === "retouch" && this.state.area === "box" && rect) {
        // The worker reads the box from params.rect; a top-level maskRect was never read, so the edit and Erase
        // silently ran on the whole image (found 2026-10-08: the ottoman stayed and the photo was regenerated).
        body.params = {
          ...params,
          rect
        };
      }
      this.runJob(() => api.runOp(this.state.family.id, body), `${nice(tool)} on v${cur.version}…`, `${nice(tool)} saved as a new version.`);
    });
    /** One color applies here; several open the set sheet (estimate, one batch, one approval) with them filled in. */
    _defineProperty(this, "makeColors", () => {
      const picks = this.colorPicks();
      if (picks.length === 1) {
        this.apply("recolor");
        return;
      }
      this.setState({
        colorwayInit: {
          attr: this.state.rcAttr,
          values: picks
        },
        colorwayOpen: true
      });
    });
    _defineProperty(this, "setArea", area => this.setState(area === "whole" ? {
      area,
      rect: null,
      strokes: [],
      rtErase: false,
      tool: this.state.tool === "retouch" ? "" : this.state.tool
    } : {
      area,
      rect: null,
      strokes: [],
      tool: "retouch",
      sideTab: "edit",
      previewKey: null
    }));
    _defineProperty(this, "pickTool", k => this.setState({
      tool: k,
      rect: null,
      strokes: [],
      previewKey: null,
      area: k === "retouch" ? "box" : "whole",
      rtErase: false
    }));
    /** Click a placement: its crop fills the canvas; click it again (or pick a tool) for the full image. */
    _defineProperty(this, "previewPlacement", key => this.setState({
      previewKey: this.state.previewKey === key ? null : key,
      tool: "",
      rect: null,
      strokes: []
    }));
    _defineProperty(this, "extendTo", p => {
      const cur = this.current();
      if (!cur) return;
      this.runJob(() => api.runOp(this.state.family.id, {
        versionId: cur.id,
        action: "extend",
        params: {
          aspect: p.aspect,
          placementKey: p.key
        }
      }), `Extend v${cur.version} to ${p.aspect}…`, `Extended to ${p.name}.`);
    });
    /* ---------- autosave (free): text layers and placement ticks ---------- */
    _defineProperty(this, "setText", (kind, value) => {
      const lang = this.state.lang;
      this.setState({
        text: {
          ...this.state.text,
          [kind]: {
            ...this.state.text[kind],
            [lang]: value
          }
        },
        saved: "Saving…"
      }, () => this.debounce("text", this.saveText, 700));
    });
    _defineProperty(this, "saveText", async () => {
      const fam = this.state.family,
        cur = this.current();
      if (!fam || !cur) return;
      const layers = this.layers();
      // keep the live preview in sync before the server answers
      const versions = fam.versions.map(v => v.id === cur.id ? {
        ...v,
        textLayers: layers
      } : v);
      this.setState({
        family: {
          ...fam,
          versions
        }
      });
      try {
        await api.putText(fam.id, cur.id, layers);
        if (this._alive) this.setState({
          saved: `Saved · v${cur.version}`
        });
      } catch (e) {
        if (this._alive) this.setState({
          saved: api.isSoft(e) ? "Text autosave not available yet" : "Not saved"
        });
      }
    });
    _defineProperty(this, "togglePlacement", async (key, on) => {
      const fam = this.state.family,
        cur = this.current();
      if (!fam || !cur) return;
      const keys = on ? Array.from(new Set([...cur.placements, key])) : cur.placements.filter(k => k !== key);
      const versions = fam.versions.map(v => v.id === cur.id ? {
        ...v,
        placements: keys
      } : v);
      this.setState({
        family: {
          ...fam,
          versions
        },
        saved: "Saving…"
      });
      this.debounce("pl", async () => {
        try {
          await api.putPlacements(fam.id, cur.id, keys);
          if (this._alive) this.setState({
            saved: `Saved · v${cur.version}`
          });
        } catch (e) {
          if (this._alive) this.setState({
            saved: api.isSoft(e) ? "Placement autosave not available yet" : "Not saved"
          });
        }
      }, 500);
    });
    /* ---------- trash ---------- */
    _defineProperty(this, "trash", async () => {
      const fam = this.state.family;
      if (!fam) return;
      try {
        await api.deleteImage(fam.id);
        toast("Moved to Trash (restore within 30 days).");
        this.props.history.push("/Content");
      } catch (e) {
        this.jobError(e, "Could not move this image to Trash.");
      }
    });
    /* ---------- first run: pick, upload, describe ---------- */
    _defineProperty(this, "loadPick", async q => {
      api.searchProducts(q, 24).then(list => {
        if (this._alive) this.setState({
          pickProducts: list || []
        });
      });
      const fams = await api.listImages({
        q,
        $top: 60
      });
      let list = fams && fams.length ? fams.map(f => ({
        id: idStr(f.id || f.rootId),
        url: f.latestUrl || f.url || f.root && f.root.url,
        title: f.title || f.root && f.root.title || "",
        source: f.root && f.root.source || f.source || "",
        product: f.product
      })) : null;
      if (!list) list = await api.listLibraryImages(q, 60);
      if (this._alive) this.setState({
        pickList: list || []
      });
    });
    _defineProperty(this, "onUpload", e => {
      const f = e.target.files && e.target.files[0];
      e.target.value = "";
      if (f) this.uploadFile(f);
    });
    _defineProperty(this, "onDrop", e => {
      e.preventDefault();
      this.setState({
        upDrag: false
      });
      if (this.state.upBusy) return;
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) this.uploadFile(f);
    });
    _defineProperty(this, "uploadFile", async f => {
      if (!/^image\//.test(f.type)) {
        this.setState({
          upErr: "Pick an image file (JPG, PNG or WEBP)."
        });
        return;
      }
      this.setState({
        upBusy: "Uploading…",
        upPct: 0,
        upErr: "",
        upSimilar: null,
        upPending: null
      });
      try {
        const r = await api.uploadAsset(f, {
          rights: this.state.upRights,
          rightsNote: this.state.upNote.trim(),
          language: this.state.language,
          rejectSimilar: true,
          onProgress: p => {
            if (this._alive) this.setState({
              upBusy: `Uploading · ${p}%`,
              upPct: p
            });
          }
        });
        if (!this._alive) return;
        if (r.duplicate && r.of) {
          toast("You already have this image: opening it.");
          this.props.history.push(`/Content/images/${r.of}`);
          return;
        }
        if (r.similar && r.similar.length) {
          // Close matches (pHash or embeddings): nothing was created (rejectSimilar); show them before deciding.
          const items = await this.hydrateSimilar(r.similar.slice(0, 6));
          if (!this._alive) return;
          this.setState({
            upSimilar: items,
            upPending: {
              url: r.url,
              title: f.name.replace(/\.[a-z0-9]+$/i, ""),
              createdId: r.id || ""
            }
          });
          return;
        }
        if (!r.id) {
          this.setState({
            upErr: "The upload finished but the asset was not registered."
          });
          return;
        }
        this.props.history.push(`/Content/images/${r.id}`);
      } catch (x) {
        if (this._alive) this.setState({
          upErr: x && x.message || "The upload did not go through."
        });
      } finally {
        if (this._alive) this.setState({
          upBusy: "",
          upPct: null
        });
      }
    });
    /** Fill thumbs and titles for pHash matches that come back as {id, distance} only. */
    _defineProperty(this, "hydrateSimilar", async items => Promise.all(items.map(async s => {
      if (s.url) return s;
      try {
        const d = await api.getImage(s.rootId || s.id);
        const root = d && (d.root || (d.versions || [])[0]);
        return {
          ...s,
          url: root && (root.latestUrl || root.url) || "",
          title: s.title || root && root.title || ""
        };
      } catch (e) {
        return s;
      }
    })));
    _defineProperty(this, "keepUpload", async () => {
      const p = this.state.upPending;
      if (!p) return;
      if (p.createdId) {
        this.props.history.push(`/Content/images/${p.createdId}`);
        return;
      }
      this.setState({
        upBusy: "Saving…",
        upErr: ""
      });
      try {
        const r = await api.registerUpload({
          url: p.url,
          title: p.title,
          rights: this.state.upRights,
          rightsNote: this.state.upNote.trim(),
          language: this.state.language
        });
        if (!this._alive) return;
        if (!r.id) {
          this.setState({
            upErr: "The upload finished but the asset was not registered."
          });
          return;
        }
        this.props.history.push(`/Content/images/${r.id}`);
      } catch (x) {
        if (this._alive) this.setState({
          upErr: x && x.message || "Could not register the upload."
        });
      } finally {
        if (this._alive) this.setState({
          upBusy: "",
          upSimilar: null,
          upPending: null
        });
      }
    });
    _defineProperty(this, "searchProducts", q => {
      this.setState({
        productQ: q
      });
      this.debounce("pq", async () => {
        const list = await api.searchProducts(q.trim());
        if (this._alive) this.setState({
          products: list || []
        });
      }, 250);
    });
    _defineProperty(this, "startFromProduct", async p => {
      if (!p.imageUrl) {
        this.setState({
          mode: "create"
        });
        await this.pickAnchor(p);
        return;
      }
      this.setState({
        upBusy: "Opening…",
        upErr: ""
      });
      try {
        const r = await api.registerUpload({
          url: p.imageUrl,
          title: p.title,
          rights: "brand_supplied",
          productId: p.id,
          source: "catalog",
          language: this.state.language
        });
        const id = r.duplicate && r.of ? r.of : r.id;
        if (id) this.props.history.push(`/Content/images/${id}`);else this.setState({
          upErr: "Could not open this product's image.",
          upBusy: ""
        });
      } catch (e) {
        if (this._alive) this.setState({
          upErr: e && e.message || "Could not open this product's image.",
          upBusy: ""
        });
      }
    });
    _defineProperty(this, "pickAnchor", async p => {
      const recipe = (await api.resolveCategory(p.id)) || null;
      const r = recipe ? api.normRecipe(recipe) : api.guessCategory(this.state.recipes, p);
      const people = this.peopleOn();
      const shots = (r.shotTypes || []).filter(s => people || !PEOPLE_KEYS.has(s));
      const scenes = (r.scenes || []).filter(s => people || !PEOPLE_KEYS.has(s));
      if (!this._alive) return;
      this.setState({
        anchor: p,
        anchorRecipe: r,
        shot: shots[0] || "",
        scene: scenes[0] || "",
        picks: (r.placementsDefault || []).slice(),
        refs: [],
        refList: null,
        products: null,
        productQ: p.title || ""
      });
      const fams = await api.listImages({
        product: p.id,
        $top: 12
      });
      let refs = fams && fams.length ? fams.map(f => ({
        id: idStr(f.id || f.rootId),
        url: f.latestUrl || f.url || f.root && f.root.url,
        title: f.title || "",
        source: f.root && f.root.source || f.source || "",
        hasFaces: !!(f.hasFaces || f.root && f.root.hasFaces)
      })) : [];
      if (!refs.length && p.imageUrl) refs = [{
        id: "",
        url: p.imageUrl,
        title: p.title,
        source: "catalog",
        hasFaces: false
      }];
      const lib = await api.listLibraryImages("", 24);
      const seen = new Set(refs.map(x => x.url));
      (lib || []).forEach(a => {
        if (!seen.has(a.url)) {
          seen.add(a.url);
          refs.push(a);
        }
      });
      if (this._alive) this.setState({
        refList: refs,
        refs: refs[0] && refs[0].id && !refs[0].hasFaces ? [refs[0].id] : []
      });
    });
    _defineProperty(this, "clearAnchor", () => {
      const r = api.guessCategory(this.state.recipes, null);
      this.setState({
        anchor: null,
        anchorRecipe: r,
        shot: "",
        scene: "",
        picks: (r.placementsDefault || []).slice(),
        refs: [],
        productQ: "",
        products: null
      });
      if (!this.state.refList) api.listLibraryImages("", 24).then(lib => {
        if (this._alive) this.setState({
          refList: lib || []
        });
      });
    });
    _defineProperty(this, "create", async () => {
      const {
        prompt,
        anchor,
        refs,
        shot,
        scene,
        look,
        shape,
        count,
        language,
        picks
      } = this.state;
      if (!prompt.trim()) return;
      this.setState({
        createBusy: "Starting…",
        createErr: "",
        pendingN: Number(count) || 1
      });
      try {
        // `references` and `shotType` are the names ImagesController.Create binds; the older names stay for compatibility.
        const r = await api.createImage({
          prompt: prompt.trim(),
          productId: anchor ? anchor.id : undefined,
          referenceAssetIds: refs,
          references: refs,
          shot: shot || undefined,
          shotType: shot || undefined,
          scene: scene || undefined,
          look: look || undefined,
          aspect: shape,
          count: Number(count) || 1,
          language,
          placements: picks
        });
        const jobId = r && (r.jobId || r.id);
        if (!jobId) throw new Error("The create did not start.");
        this.setState({
          createBusy: count > 1 ? `Making ${count} previews…` : "Making the preview…"
        });
        const done = await api.waitJob(jobId, this.alive, 60);
        if (!this._alive) return;
        if (done.status === "done") {
          const ids = [];
          (done.outputVersionIds && done.outputVersionIds.length ? done.outputVersionIds : [done.outputVersionId]).forEach(x => {
            if (x && ids.indexOf(x) === -1) ids.push(x);
          });
          if (!ids.length) {
            this.setState({
              createErr: "The previews finished but no image came back."
            });
            return;
          }
          // The job carries ids only: each output is its own image, so read each one for its preview URL.
          const items = await Promise.all(ids.map(id => api.getImage(id).then(d => previewOf(id, d)).catch(() => ({
            id,
            url: ""
          }))));
          if (!this._alive) return;
          const shown = items.filter(x => x.url);
          if (!shown.length) {
            this.props.history.push(`/Content/images/${ids[0]}`);
            return;
          }
          const have = new Set(this.state.previews.map(x => x.id));
          this.setState({
            previews: [...this.state.previews, ...shown.filter(x => !have.has(x.id))],
            selId: shown[0].id
          }, () => {
            const el = document.getElementById(`in-prev-${shown[0].id}`);
            if (el && this.state.previews.length > shown.length && el.scrollIntoView) el.scrollIntoView({
              behavior: "smooth",
              block: "nearest"
            });
          });
        } else if (done.status === "timeout") this.setState({
          createErr: "Still rendering: the previews appear in Assets when they finish."
        });else this.setState({
          createErr: done.error || "The previews failed."
        });
      } catch (e) {
        if (api.isSoft(e)) this.setState({
          createErr: "Creating from a prompt is not available yet."
        });else if (e && e.status === 402) this.setState({
          credits: {
            insufficient: true,
            buyUrl: e.data && e.data.buyUrl || "https://account.plunk.io/?from=app"
          },
          createErr: ""
        });else this.setState({
          createErr: e && e.message || "Could not start the previews."
        });
      } finally {
        if (this._alive) this.setState({
          createBusy: "",
          pendingN: 0
        });
      }
    });
    _defineProperty(this, "openPreview", id => {
      const pick = id || this.state.selId || this.state.previews[0] && this.state.previews[0].id;
      if (pick) this.props.history.push(`/Content/images/${pick}`);
    });
    /** Add or remove a "keep exact" photo (up to 4; faces are never sent). */
    _defineProperty(this, "toggleRef", a => {
      if (!a || !a.id || a.hasFaces) return;
      const on = this.state.refs.indexOf(a.id) !== -1;
      const refList = this.state.refList || [];
      this.setState({
        refs: on ? this.state.refs.filter(x => x !== a.id) : [...this.state.refs, a.id].slice(0, 4),
        refList: refList.some(x => x.id === a.id) ? refList : [...refList, a]
      });
    });
    _defineProperty(this, "searchRefs", async q => {
      if (!q) {
        this.setState({
          refSearch: null
        });
        return;
      }
      const list = await api.listLibraryImages(q, 48);
      if (this._alive) this.setState({
        refSearch: list || []
      });
    });
    _defineProperty(this, "openRefPicker", () => {
      this.setState({
        refPicker: true,
        refSearch: null
      });
      if (!this.state.refList) api.listLibraryImages("", 24).then(lib => {
        if (this._alive) this.setState({
          refList: lib || []
        });
      });
    });
    /** After a Shopify push: the version is Published in the stepper right away; the family and the Shopify rows reload behind it. */
    _defineProperty(this, "onPushed", r => {
      const fam = this.state.family;
      if (!fam) return;
      const cur = this.current();
      const pushedId = idStr(r && r.version && r.version.id || cur && cur.id);
      const versions = fam.versions.map(v => v.id === pushedId ? {
        ...v,
        approvalState: "published",
        hasHires: true
      } : v);
      this.setState({
        family: {
          ...fam,
          versions
        }
      });
      this.loadFamily(fam.id, pushedId);
    });
  }
  componentDidMount() {
    this.boot();
  }
  componentDidUpdate(prev) {
    if (this.imageId(prev) !== this.imageId() || this.search(prev) !== this.search()) this.boot();
  }
  componentWillUnmount() {
    this._alive = false;
    Object.values(this._timers).forEach(clearTimeout);
  }
  imageId(p = this.props) {
    const id = p.params && p.params.id;
    return id && id !== "new" ? String(id) : null;
  }
  search(p = this.props) {
    return p.location && p.location.search || "";
  }
  query() {
    const q = {};
    this.search().replace(/^\?/, "").split("&").forEach(kv => {
      const [k, v] = kv.split("=");
      if (k) q[decodeURIComponent(k)] = decodeURIComponent(v || "");
    });
    return q;
  }
  /** The way named in the URL; no mode means Describe it. */
  queryMode() {
    const m = this.query().mode;
    return ["create", "pick", "upload"].indexOf(m) !== -1 ? m : "create";
  }
  peopleOn() {
    const s = this.state.settings;
    return !!(s && s.people && s.people.enabled);
  }
  optionsOf(p) {
    if (!p) return {};
    if (p.options && !Array.isArray(p.options) && typeof p.options === "object") return p.options;
    if (Array.isArray(p.options)) {
      const o = {};
      p.options.forEach(x => {
        if (x && x.name) o[x.name] = x.values || [];
      });
      return o;
    }
    if (Array.isArray(p.variantOptions)) {
      const o = {};
      p.variantOptions.forEach(x => {
        if (x && x.name) o[x.name] = x.values || [];
      });
      return o;
    }
    const o = {};
    ["Color", "Material", "Pattern", "Finish", "Size"].forEach(k => {
      const v = p[k] || p[k.toLowerCase()];
      if (Array.isArray(v) && v.length) o[k] = v;else if (typeof v === "string" && v) o[k] = [v];
    });
    return o;
  }
  current() {
    const f = this.state.family;
    if (!f) return null;
    return f.versions.find(v => v.id === this.state.currentId) || f.versions[f.versions.length - 1];
  }
  syncText() {
    const cur = this.current();
    if (!cur) return;
    const m = layersToMap(cur.textLayers);
    const base = this.baseLang(cur);
    const lang = this.langs(cur).indexOf(this.state.lang) !== -1 ? this.state.lang : base;
    this.setState({
      text: {
        headline: {
          ...(m.headline && m.headline.text || {})
        },
        sub: {
          ...(m.sub && m.sub.text || {})
        },
        cta: {
          ...(m.cta && m.cta.text || {})
        }
      },
      font: m.headline && m.headline.font || "brand",
      fontId: m.headline && m.headline.fontId || null,
      color: m.headline && m.headline.color || "white_on_dark",
      lang
    });
  }
  baseLang(cur) {
    const v = cur || this.current();
    return String(v && v.language || "en").toLowerCase();
  }
  /** Languages on the text layer: the base, every language with text, and the ones added this session. */
  langs(cur) {
    const v = cur || this.current();
    const out = [this.baseLang(v)];
    [...layerLangs(v && v.textLayers), ...this.state.addedLangs].forEach(k => {
      if (out.indexOf(k) === -1) out.push(k);
    });
    return out;
  }
  missingLangs(cur) {
    const v = cur || this.current();
    const base = this.baseLang(v);
    return this.langs(v).filter(k => k !== base && !(v.textLayers || []).some(l => l && l.text && l.text[k]));
  }
  /** "from v2" only when the version branches off something other than the version right before it. */
  branchFrom(v) {
    const f = this.state.family;
    if (!f || !v.parentVersionId) return null;
    const p = f.versions.find(x => x.id === v.parentVersionId);
    return p && p.version !== v.version - 1 ? `from v${p.version}` : null;
  }
  depth(v) {
    const f = this.state.family;
    let d = 0,
      p = v;
    const seen = new Set();
    while (p && p.parentVersionId && !seen.has(p.id)) {
      seen.add(p.id);
      p = f.versions.find(x => x.id === p.parentVersionId);
      if (p) d++;
    }
    return d;
  }
  chain(v) {
    const f = this.state.family;
    const out = [];
    let p = v;
    const seen = new Set();
    while (p && !seen.has(p.id)) {
      seen.add(p.id);
      out.push(p);
      p = p.parentVersionId ? f.versions.find(x => x.id === p.parentVersionId) : null;
    }
    return out;
  }
  canApply(forced) {
    const tool = typeof forced === "string" && forced || this.state.tool;
    const {
      busy,
      rtPrompt,
      rtErase,
      rect,
      bgMode,
      bgScene,
      area,
      strokes
    } = this.state;
    if (busy || !this.current()) return false;
    if (tool === "retouch") {
      const masked = area === "brush" ? strokes.length > 0 : area === "box" ? !!rect : false;
      return rtErase && area !== "whole" ? masked : rtPrompt.trim().length > 2;
    }
    if (tool === "recolor") return this.colorPicks().length === 1;
    if (tool === "background") return bgMode !== "scene" || !!(bgScene || ((this.state.family.category || {}).scenes || []).length);
    if (tool === "crop") return !!rect;
    if (tool === "badge") return this.state.bdText.trim().length > 0 && this.state.bdText.trim().length <= 24;
    return tool !== "text";
  }
  freeColors() {
    return String(this.state.rcFree || "").split(/[,\n]/).map(v => v.trim()).filter(Boolean);
  }
  /** Colors picked in the Colors tool: catalog values when the product has options, else what was typed. */
  colorPicks() {
    return Object.keys(this.state.options || {}).length ? this.state.rcPicks : this.freeColors();
  }
  /** Canvas width for an image of this shape: as wide as the column allows, but never taller than ~620px. */
  canvasMax(aspect) {
    const a = Number(aspect) > 0 ? Number(aspect) : 1;
    return `min(100%, ${Math.round(620 * a)}px)`;
  }
  layers() {
    const {
      text,
      font,
      fontId,
      color
    } = this.state;
    return ["headline", "sub", "cta"].filter(k => Object.values(text[k] || {}).some(v => v && v.trim())).map(k => ({
      kind: k,
      text: Object.fromEntries(Object.entries(text[k]).filter(([, v]) => v && v.trim())),
      font,
      fontId: fontId || undefined,
      color
    }));
  }
  debounce(k, fn, ms) {
    clearTimeout(this._timers[k]);
    this._timers[k] = setTimeout(fn, ms);
  }
  /* =================================================================== render */
  render() {
    const id = this.imageId();
    const crumbs = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("a", {
      href: "#",
      onClick: e => {
        e.preventDefault();
        this.props.history.push("/Content");
      }
    }, "Content Hub"), " \u203A ", /*#__PURE__*/React.createElement("a", {
      href: "#",
      onClick: e => {
        e.preventDefault();
        this.props.history.push("/Content");
      }
    }, "Assets"), " \u203A Image workspace");
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view iw-view ec-form"
    }, !id ? this.renderNew() : this.renderWorkspace(crumbs), id && this.state.credits && this.state.credits.insufficient ? this.renderCreditsNotice() : null);
  }
  /* ---------- 1 · new image: Describe it · Start from a product · Upload a photo (one shell, the way in the URL) ---------- */
  renderNew() {
    const s = this.state;
    const mode = s.mode;
    let actions = null;
    if (mode === "create") {
      const has = s.previews.length > 0;
      const n = Number(s.count) || 1;
      actions = /*#__PURE__*/React.createElement(React.Fragment, null, has ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "iwx-btn in-btn",
        disabled: !!s.createBusy || !s.prompt.trim(),
        onClick: this.create
      }, s.createBusy ? "Making…" : n > 1 ? `Make ${n} more` : "Make another") : null, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "in-primary",
        disabled: !has,
        title: has ? undefined : "Make previews first, then open the one you like",
        onClick: () => this.openPreview()
      }, "Open in the editor"));
    }
    const pickList = s.refSearch !== null ? s.refSearch : s.refList;
    return /*#__PURE__*/React.createElement("div", {
      className: "iwx in-view"
    }, /*#__PURE__*/React.createElement(NewTop, {
      mode: mode,
      onBack: () => this.props.history.push("/Content"),
      onMode: this.goMode
    }, actions), mode === "pick" ? this.renderNewPick() : mode === "upload" ? this.renderNewUpload() : this.renderNewCreate(), s.refPicker ? /*#__PURE__*/React.createElement(RefPicker, {
      list: pickList,
      refs: s.refs,
      max: 4,
      onToggle: this.toggleRef,
      onSearch: this.searchRefs,
      onClose: () => this.setState({
        refPicker: false,
        refSearch: null
      })
    }) : null);
  }
  renderCreditsNotice() {
    const c = this.state.credits;
    return /*#__PURE__*/React.createElement("div", {
      className: "iw-notice",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for this action."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(c.buyUrl || "https://account.plunk.io/?from=app", "_blank", "noopener")
    }, "Buy credits"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      onClick: () => this.setState({
        credits: null
      })
    }, "Dismiss"));
  }
  /* Describe it: prompt card and previews on the stage, the choices in the right rail. */
  renderNewCreate() {
    const s = this.state;
    const r = s.anchorRecipe || api.guessCategory(s.recipes, null);
    const people = this.peopleOn();
    const busy = !!s.createBusy;
    const n = Number(s.count) || 1;
    const has = s.previews.length > 0;
    const shotList = s.anchor ? (r.shotTypes || []).map(k => [k, nice(k)]) : [];
    const sceneList = s.anchor ? (r.scenes || []).map(k => [k, nice(k)]) : [];
    const offPeople = k => !people && PEOPLE_KEYS.has(k);
    const offTitle = "Shots with people are off for this workspace";
    const hasPeopleChoice = [...shotList, ...sceneList].some(([k]) => PEOPLE_KEYS.has(k));
    const peopleChosen = PEOPLE_KEYS.has(s.shot) || PEOPLE_KEYS.has(s.scene);
    const placements = s.placements || api.FALLBACK_PLACEMENTS;
    const ratio = api.aspectNum(s.shape);
    const byId = {};
    (s.refList || []).forEach(a => {
      if (a.id) byId[a.id] = a;
    });
    const canMake = !busy && !!s.prompt.trim();
    const makeLabel = n > 1 ? `Make ${n} previews` : "Make a preview";
    const tiles = has ? s.previews.length + (busy ? s.pendingN : 0) : busy ? s.pendingN : n;
    return /*#__PURE__*/React.createElement("div", {
      className: "in-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "in-stage"
    }, /*#__PURE__*/React.createElement("div", {
      className: "in-prompt"
    }, /*#__PURE__*/React.createElement("label", {
      className: "in-sr",
      htmlFor: "in-prompt-text"
    }, "Describe the image"), /*#__PURE__*/React.createElement("textarea", {
      id: "in-prompt-text",
      rows: 2,
      value: s.prompt,
      disabled: busy,
      placeholder: s.anchor ? `The ${s.anchor.title} on a clean surface, soft morning light, editorial, no text` : "Our product on a clean surface, soft morning light, editorial, no text",
      onChange: e => this.setState({
        prompt: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canMake) {
          e.preventDefault();
          this.create();
        }
      }
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "in-make",
      disabled: !canMake,
      onClick: this.create
    }, busy ? s.createBusy || "Making…" : makeLabel)), s.createErr ? /*#__PURE__*/React.createElement("p", {
      className: "in-err",
      role: "alert"
    }, s.createErr) : null, s.credits && s.credits.insufficient ? this.renderCreditsNotice() : null, /*#__PURE__*/React.createElement("div", {
      className: "in-head"
    }, /*#__PURE__*/React.createElement("b", null, has ? "Pick the one you like" : "Your previews appear here"), /*#__PURE__*/React.createElement("span", null, has ? "Previews are quick. The full-size image is made when you open it." : `Describe the image above, then choose ${makeLabel}.`)), /*#__PURE__*/React.createElement("div", {
      className: "in-grid" + (tiles <= 1 ? " one" : "") + (api.aspectNum(s.shape) < 1 ? " tall" : ""),
      style: {
        "--in-ar": ratio
      },
      "aria-busy": busy
    }, s.previews.map((p, i) => {
      const on = p.id === s.selId;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        id: `in-prev-${p.id}`,
        key: p.id,
        className: "in-tile" + (on ? " on" : ""),
        "aria-pressed": on,
        "aria-label": `Preview ${i + 1}`,
        onClick: () => this.setState({
          selId: p.id
        }),
        onDoubleClick: () => this.openPreview(p.id)
      }, /*#__PURE__*/React.createElement("img", {
        src: p.url,
        alt: "",
        draggable: false
      }), on ? /*#__PURE__*/React.createElement("span", {
        className: "sel"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        "aria-hidden": "true"
      }, "check"), "Selected") : null);
    }), busy || !has ? /*#__PURE__*/React.createElement(PlaceholderTiles, {
      n: has ? s.pendingN : busy ? s.pendingN : n,
      ratio: ratio,
      busy: busy,
      label: s.createBusy
    }) : null), has ? /*#__PURE__*/React.createElement("p", {
      className: "in-muted in-tip"
    }, "Double-click a preview to open it. Every preview is kept in Assets.") : null), /*#__PURE__*/React.createElement("aside", {
      className: "in-rail",
      "aria-label": "Image options"
    }, /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-keep",
      title: "Keep something exact",
      note: "optional"
    }, /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "Add a product or a photo and it stays true in every preview."), s.anchor ? /*#__PURE__*/React.createElement("div", {
      className: "in-item"
    }, /*#__PURE__*/React.createElement("span", {
      className: "th"
    }, s.anchor.imageUrl ? /*#__PURE__*/React.createElement("img", {
      src: s.anchor.imageUrl,
      alt: ""
    }) : /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "inventory_2")), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, /*#__PURE__*/React.createElement("b", null, s.anchor.title || "Product"), /*#__PURE__*/React.createElement("small", null, ["Product", s.anchor.productType].filter(Boolean).join(" · "))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "in-x",
      "aria-label": `Remove ${s.anchor.title || "the product"}`,
      title: "Remove",
      disabled: busy,
      onClick: this.clearAnchor
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "close"))) : /*#__PURE__*/React.createElement("div", {
      className: "in-find"
    }, /*#__PURE__*/React.createElement("label", {
      className: "in-search sm"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "search"), /*#__PURE__*/React.createElement("span", {
      className: "in-sr"
    }, "Search your products"), /*#__PURE__*/React.createElement("input", {
      value: s.productQ,
      disabled: busy,
      placeholder: "Search your products by name or SKU",
      onChange: e => this.searchProducts(e.target.value)
    })), s.products && s.productQ.trim() ? s.products.length ? /*#__PURE__*/React.createElement("ul", {
      className: "in-results",
      "aria-label": "Matching products"
    }, s.products.slice(0, 6).map(p => /*#__PURE__*/React.createElement("li", {
      key: p.id
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "in-item pick",
      onClick: () => this.pickAnchor(p)
    }, /*#__PURE__*/React.createElement("span", {
      className: "th"
    }, p.imageUrl ? /*#__PURE__*/React.createElement("img", {
      src: p.imageUrl,
      alt: "",
      loading: "lazy"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "inventory_2")), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, /*#__PURE__*/React.createElement("b", null, p.title || "Untitled product"), /*#__PURE__*/React.createElement("small", null, p.productType || "Product")))))) : /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "No products match. You can still describe the image without one.") : null), /*#__PURE__*/React.createElement("div", {
      className: "in-slots",
      role: "group",
      "aria-label": "Photos to keep"
    }, s.refs.map(id => {
      const a = byId[id] || {
        id,
        url: "",
        title: ""
      };
      return /*#__PURE__*/React.createElement("span", {
        className: "in-slot",
        key: id,
        title: a.title || "Photo"
      }, a.url ? /*#__PURE__*/React.createElement("img", {
        src: a.url,
        alt: ""
      }) : /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        "aria-hidden": "true"
      }, "image"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "in-x sm",
        "aria-label": `Remove ${a.title || "this photo"}`,
        disabled: busy,
        onClick: () => this.toggleRef(a)
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        "aria-hidden": "true"
      }, "close")));
    }), s.refs.length < 4 ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "in-slot add",
      disabled: busy,
      onClick: this.openRefPicker
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "add"), "Add") : null), s.refs.length >= 4 ? /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "Up to 4 photos.") : null), /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-shape",
      title: "Shape"
    }, /*#__PURE__*/React.createElement(Segs, {
      label: "Shape",
      list: SHAPES,
      value: s.shape,
      disabled: busy,
      onChange: k => this.setState({
        shape: k
      })
    }), /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "Crops for each channel come later in the editor.")), /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-look",
      title: "Look"
    }, /*#__PURE__*/React.createElement(Chips, {
      label: "Look",
      list: LOOKS,
      value: s.look,
      disabled: busy,
      onChange: k => this.setState({
        look: s.look === k ? "" : k
      })
    }), /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, s.look ? "Click it again to let the description decide." : "Optional. Without one, the description decides.")), shotList.length ? /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-shot",
      title: "Kind of shot"
    }, /*#__PURE__*/React.createElement(Chips, {
      label: "Kind of shot",
      list: shotList,
      value: s.shot,
      disabled: busy,
      off: offPeople,
      offTitle: offTitle,
      onChange: k => this.setState({
        shot: k
      })
    })) : null, sceneList.length ? /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-scene",
      title: "Setting"
    }, /*#__PURE__*/React.createElement(Chips, {
      label: "Setting",
      list: sceneList,
      value: s.scene,
      disabled: busy,
      off: offPeople,
      offTitle: offTitle,
      onChange: k => this.setState({
        scene: k
      })
    })) : null, hasPeopleChoice && !people ? /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "Shots with people are off. An admin can turn them on in Manage Data \u203A Content settings.") : null, people && peopleChosen ? /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "People here are synthetic, never a real person, and the image is marked AI-made.") : null, /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-count",
      title: "How many"
    }, /*#__PURE__*/React.createElement(Segs, {
      label: "How many previews",
      list: COUNTS,
      value: Number(s.count),
      disabled: busy,
      onChange: k => this.setState({
        count: k
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "in-more"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "in-more-btn",
      "aria-expanded": !!s.moreOpts,
      "aria-controls": "in-more-body",
      onClick: () => this.setState({
        moreOpts: !s.moreOpts
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, s.moreOpts ? "expand_less" : "expand_more"), "More options"), s.moreOpts ? /*#__PURE__*/React.createElement("div", {
      id: "in-more-body",
      className: "in-more-body"
    }, /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-lang",
      title: "Language",
      note: "for any text in the image"
    }, /*#__PURE__*/React.createElement(Segs, {
      label: "Language",
      list: LANGS,
      value: s.language,
      disabled: busy,
      onChange: k => this.setState({
        language: k
      })
    })), /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-sizes",
      title: "Sizes to check"
    }, /*#__PURE__*/React.createElement(Chips, {
      multi: true,
      label: "Sizes to check",
      list: placements.map(p => [p.key, p.name]),
      value: s.picks,
      disabled: busy,
      onChange: k => this.setState({
        picks: s.picks.indexOf(k) !== -1 ? s.picks.filter(x => x !== k) : [...s.picks, k]
      })
    }), /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "These show in the editor with their safe areas. Change them any time."))) : null), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement("p", {
      className: "in-foot"
    }, "The image opens in the editor, where you can erase, extend, recolor, add text and export every size.")));
  }

  /* Start from a product: search on the stage, products then your library. */
  renderNewPick() {
    const {
      pickList,
      pickQ,
      upBusy,
      upErr,
      pickProducts
    } = this.state;
    const skeleton = k => /*#__PURE__*/React.createElement("div", {
      className: "in-libgrid",
      "aria-hidden": "true"
    }, [0, 1, 2, 3, 4, 5].map(i => /*#__PURE__*/React.createElement("span", {
      key: k + i,
      className: "in-libtile ghost"
    }, /*#__PURE__*/React.createElement("span", {
      className: "im"
    }), /*#__PURE__*/React.createElement("span", {
      className: "cap"
    }, /*#__PURE__*/React.createElement("i", null)))));
    return /*#__PURE__*/React.createElement("div", {
      className: "in-body solo"
    }, /*#__PURE__*/React.createElement("div", {
      className: "in-stage"
    }, /*#__PURE__*/React.createElement("label", {
      className: "in-search lg"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "search"), /*#__PURE__*/React.createElement("span", {
      className: "in-sr"
    }, "Search products and images"), /*#__PURE__*/React.createElement("input", {
      value: pickQ,
      autoFocus: true,
      placeholder: "Search products by name or SKU, or images by title",
      onChange: e => {
        this.setState({
          pickQ: e.target.value
        });
        this.debounce("pick", () => this.loadPick(e.target.value.trim()), 250);
      }
    })), upErr ? /*#__PURE__*/React.createElement("p", {
      className: "in-err",
      role: "alert"
    }, upErr) : null, upBusy ? /*#__PURE__*/React.createElement("p", {
      className: "in-status",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "autorenew"), upBusy) : null, /*#__PURE__*/React.createElement("div", {
      className: "in-head"
    }, /*#__PURE__*/React.createElement("b", null, "Products"), /*#__PURE__*/React.createElement("span", null, pickProducts === null ? "Loading…" : pickProducts.length ? "The product photo becomes the first version, with the product's details attached." : "No products match.")), pickProducts === null ? skeleton("p") : pickProducts.length ? /*#__PURE__*/React.createElement("div", {
      className: "in-libgrid"
    }, pickProducts.map(p => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: p.id,
      className: "in-libtile prod",
      disabled: !!upBusy,
      title: p.title,
      onClick: () => this.startFromProduct(p)
    }, /*#__PURE__*/React.createElement("span", {
      className: "im"
    }, p.imageUrl ? /*#__PURE__*/React.createElement("img", {
      src: p.imageUrl,
      alt: "",
      loading: "lazy"
    }) : /*#__PURE__*/React.createElement("span", {
      className: "none"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "image_not_supported"), "No photo yet")), /*#__PURE__*/React.createElement("span", {
      className: "cap"
    }, /*#__PURE__*/React.createElement("b", null, p.title || "Untitled product"), /*#__PURE__*/React.createElement("small", null, p.imageUrl ? p.productType || "Product" : "Opens in Describe it"))))) : null, /*#__PURE__*/React.createElement("div", {
      className: "in-head"
    }, /*#__PURE__*/React.createElement("b", null, "Your library"), /*#__PURE__*/React.createElement("span", null, "Images already in Assets.")), pickList === null ? skeleton("l") : !pickList.length ? /*#__PURE__*/React.createElement("p", {
      className: "in-muted"
    }, "No images yet. Upload a photo or describe one.") : /*#__PURE__*/React.createElement("div", {
      className: "in-libgrid"
    }, pickList.map(a => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: a.id || a.url,
      className: "in-libtile",
      disabled: !!upBusy,
      title: a.title,
      onClick: () => this.props.history.push(`/Content/images/${a.id}`)
    }, /*#__PURE__*/React.createElement("span", {
      className: "im"
    }, /*#__PURE__*/React.createElement("img", {
      src: a.url,
      alt: "",
      loading: "lazy"
    })), a.source ? /*#__PURE__*/React.createElement("span", {
      className: "tag"
    }, sourceLabel(a.source)) : null, /*#__PURE__*/React.createElement("span", {
      className: "cap"
    }, /*#__PURE__*/React.createElement("b", null, a.title || "Untitled")))))));
  }

  /* Upload a photo: a big drop zone on the stage, the rights in the rail. */
  renderNewUpload() {
    const {
      upBusy,
      upErr,
      upRights,
      upNote,
      upDrag,
      upPct,
      upSimilar
    } = this.state;
    const busy = !!upBusy;
    return /*#__PURE__*/React.createElement("div", {
      className: "in-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "in-stage center"
    }, upSimilar && upSimilar.length ? /*#__PURE__*/React.createElement("div", {
      className: "in-card"
    }, /*#__PURE__*/React.createElement(DupNotice, {
      items: upSimilar,
      busy: busy,
      onKeep: this.keepUpload,
      onOpen: x => this.props.history.push(`/Content/images/${x.rootId || x.id}`),
      onCancel: () => this.setState({
        upSimilar: null,
        upPending: null
      })
    })) : /*#__PURE__*/React.createElement("div", {
      className: "in-drop" + (upDrag ? " over" : "") + (busy ? " busy" : ""),
      onDragOver: e => {
        e.preventDefault();
        if (!upDrag && !busy) this.setState({
          upDrag: true
        });
      },
      onDragLeave: e => {
        if (!e.currentTarget.contains(e.relatedTarget)) this.setState({
          upDrag: false
        });
      },
      onDrop: this.onDrop
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons ic",
      "aria-hidden": "true"
    }, busy ? "cloud_upload" : "add_photo_alternate"), busy ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", {
      role: "status"
    }, upBusy), /*#__PURE__*/React.createElement("span", {
      className: "in-bar",
      role: "progressbar",
      "aria-label": "Upload progress",
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "aria-valuenow": upPct || 0
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${upPct || 0}%`
      }
    }))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, "Drop a photo here"), /*#__PURE__*/React.createElement("span", {
      className: "in-muted"
    }, "or"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "in-primary",
      onClick: () => this._file && this._file.click()
    }, "Choose a photo"), /*#__PURE__*/React.createElement("small", null, "JPG, PNG or WEBP")), /*#__PURE__*/React.createElement("input", {
      type: "file",
      accept: "image/*",
      style: {
        display: "none"
      },
      ref: r => {
        this._file = r;
      },
      onChange: this.onUpload
    })), upErr ? /*#__PURE__*/React.createElement("p", {
      className: "in-err",
      role: "alert"
    }, upErr) : null), /*#__PURE__*/React.createElement("aside", {
      className: "in-rail",
      "aria-label": "Upload details"
    }, /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-rights",
      title: "Who owns the rights",
      note: "required"
    }, /*#__PURE__*/React.createElement("div", {
      className: "in-radios",
      role: "radiogroup",
      "aria-labelledby": "in-s-rights"
    }, RIGHTS.map(([k, l, sub]) => /*#__PURE__*/React.createElement("label", {
      key: k,
      className: "in-radio" + (upRights === k ? " on" : "")
    }, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "in-rights",
      value: k,
      checked: upRights === k,
      disabled: busy,
      onChange: () => this.setState({
        upRights: k
      })
    }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, l), /*#__PURE__*/React.createElement("small", null, sub)))))), /*#__PURE__*/React.createElement(Sect, {
      id: "in-s-note",
      title: "Licence or supplier note",
      note: "optional"
    }, /*#__PURE__*/React.createElement("label", {
      className: "in-sr",
      htmlFor: "in-up-note"
    }, "Licence or supplier note"), /*#__PURE__*/React.createElement("input", {
      id: "in-up-note",
      className: "in-input",
      value: upNote,
      disabled: busy,
      placeholder: "For example, a licence number or who supplied it",
      onChange: e => this.setState({
        upNote: e.target.value
      })
    })), /*#__PURE__*/React.createElement("p", {
      className: "in-note"
    }, "Exact copies open the image you already have. Close matches are shown before anything is saved."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement("p", {
      className: "in-foot"
    }, "The image opens in the editor, where you can erase, extend, recolor, add text and export every size.")));
  }

  /* ---------- 3 · workspace ---------- */
  renderWorkspace(crumbs) {
    const {
      family,
      loadErr
    } = this.state;
    if (family === undefined) return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: crumbs,
      title: "Image workspace"
    }), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Opening the image\u2026"));
    if (!family) return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: crumbs,
      title: "Image workspace"
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, loadErr === "soft" ? "The image workspace is not available yet on this environment. Edit Mode under Assets still works." : loadErr || "Could not open this image."), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.props.history.push("/Content")
    }, "Back to Assets")));
    const cur = this.current();
    const root = family.root || family.versions[0];
    const product = family.product;
    const placements = this.state.placements || api.FALLBACK_PLACEMENTS;
    const title = root && root.title || product && product.title || "Image";
    const {
      busy,
      tool,
      rect,
      compare,
      lang,
      saved,
      approveOpen,
      trashConfirm,
      area
    } = this.state;
    const preview = this.state.previewKey ? placements.find(p => p.key === this.state.previewKey) || null : null;
    const both = api.hasBothApps();
    const aspect = cur.width && cur.height ? cur.width / cur.height : 1;
    const fit = a => ({
      maxWidth: `min(100%, calc(${(Number(a) > 0 ? Number(a) : 1).toFixed(4)} * min(560px, 58vh)))`
    });
    const drawing = tool === "retouch" && area !== "whole";
    return /*#__PURE__*/React.createElement("div", {
      className: "iwx"
    }, /*#__PURE__*/React.createElement("div", {
      className: "iwx-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iwx-iconbtn",
      "aria-label": this._back ? "Back to the piece" : "Back to Assets",
      title: this._back ? "Back to the piece" : "Back to Assets",
      onClick: () => this.props.history.push(this._back || "/Content")
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "chevron_left")), /*#__PURE__*/React.createElement("div", {
      className: "iwx-title"
    }, /*#__PURE__*/React.createElement("b", null, title), /*#__PURE__*/React.createElement("span", null, "v", cur.version, " of ", family.versions.length, cur.width && cur.height ? ` · ${cur.width} × ${cur.height}` : "", " \xB7 ", opLabel(cur))), /*#__PURE__*/React.createElement("span", {
      className: "plk-chip " + approvalTone(cur.approvalState)
    }, approvalLabel(cur.approvalState)), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement("span", {
      className: "iwx-saved" + (saved && /^Saved/.test(saved) ? "" : " dim")
    }, saved && /^Saved/.test(saved) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "check"), "Saved") : saved), trashConfirm ? /*#__PURE__*/React.createElement("span", {
      className: "iw-confirm"
    }, /*#__PURE__*/React.createElement("span", null, "Move this image and its versions to Trash?"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm danger",
      onClick: this.trash
    }, "Trash"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      onClick: () => this.setState({
        trashConfirm: false
      })
    }, "Keep")) : /*#__PURE__*/React.createElement("span", {
      className: "iwx-more"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iwx-iconbtn",
      "aria-label": "More actions",
      title: "More actions",
      onClick: () => this.setState({
        moreOpen: !this.state.moreOpen
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "more_horiz")), this.state.moreOpen ? /*#__PURE__*/React.createElement("span", {
      className: "iwx-menu",
      onMouseLeave: () => this.setState({
        moreOpen: false
      })
    }, both ? /*#__PURE__*/React.createElement("a", {
      href: `${api.studioUrl()}/?asset=${encodeURIComponent(cur.id)}`,
      target: "_blank",
      rel: "noreferrer",
      onClick: () => this.setState({
        moreOpen: false
      })
    }, "Open in Studio") : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      disabled: !!busy,
      onClick: () => this.setState({
        moreOpen: false,
        trashConfirm: true
      })
    }, "Move to Trash")) : null), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iwx-btn" + (compare ? " on" : ""),
      disabled: !root || root.id === cur.id,
      title: root && root.id === cur.id ? "This is the original" : "Show the original beside this version",
      onClick: () => this.setState({
        compare: !compare,
        previewKey: null
      })
    }, "Compare with original"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy,
      onClick: () => this.setState({
        approveOpen: true
      })
    }, "Approve and export")), /*#__PURE__*/React.createElement("div", {
      className: "iwx-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "iwx-left"
    }, /*#__PURE__*/React.createElement("div", {
      className: "iwx-stage"
    }, preview && !compare ? /*#__PURE__*/React.createElement("div", {
      className: "iwx-col iw-preview",
      style: fit(api.aspectNum(preview.aspect))
    }, /*#__PURE__*/React.createElement("span", {
      className: "iwx-cap"
    }, /*#__PURE__*/React.createElement("b", null, preview.name), " \xB7 ", preview.width, " \xD7 ", preview.height, " \xB7 how this channel shows v", cur.version, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("small", null, "Dashed line: safe area \xB7 Hatched: covered by the channel")), /*#__PURE__*/React.createElement(PlacementFrame, {
      p: preview,
      version: cur,
      lang: lang,
      base: this.baseLang(cur),
      scale: 1,
      style: {
        width: "100%"
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "iwx-bar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "iwx-dotc" + (api.placementFit(cur, preview).fits ? " ok" : "")
    }), /*#__PURE__*/React.createElement("span", {
      className: "t"
    }, api.placementFit(cur, preview).fits ? "Fits this size." : "Cut off or crowded at the edges in this size."), !api.placementFit(cur, preview).fits ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iwx-go",
      disabled: !!busy,
      onClick: () => this.extendTo(preview)
    }, "Extend to fit") : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iwx-btn",
      onClick: () => this.setState({
        previewKey: null
      })
    }, "Back to the full image"))) : /*#__PURE__*/React.createElement("div", {
      className: "iwx-col" + (compare ? " compare" : ""),
      style: compare ? undefined : fit(aspect)
    }, /*#__PURE__*/React.createElement("div", {
      className: compare ? "iwx-pair" : undefined
    }, compare && root && root.id !== cur.id ? /*#__PURE__*/React.createElement("div", {
      className: "iw-canvas still"
    }, /*#__PURE__*/React.createElement("img", {
      src: root.url,
      alt: ""
    }), /*#__PURE__*/React.createElement("span", {
      className: "iw-cmp"
    }, "Original \xB7 v", root.version)) : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-canvas",
      style: {
        aspectRatio: cur.width && cur.height ? `${cur.width} / ${cur.height}` : undefined
      }
    }, tool === "retouch" && area === "brush" ? /*#__PURE__*/React.createElement(BrushCanvas, {
      src: cur.url,
      strokes: this.state.strokes,
      size: this.state.brushSize,
      enabled: !busy,
      onStrokes: st => this.setState({
        strokes: st
      }),
      onNatural: n => this.setState({
        natural: n
      })
    }) : drawing || tool === "crop" ? /*#__PURE__*/React.createElement(RectCanvas, {
      src: cur.url,
      rect: rect,
      enabled: !busy,
      onRect: r => this.setState({
        rect: r
      })
    }) : /*#__PURE__*/React.createElement("img", {
      src: cur.url,
      alt: "",
      draggable: false
    }), tool !== "retouch" ? /*#__PURE__*/React.createElement(TextOverlay, {
      layers: cur.textLayers,
      lang: lang,
      base: this.baseLang(cur)
    }) : null, compare ? /*#__PURE__*/React.createElement("span", {
      className: "iw-cmp"
    }, "v", cur.version) : null, drawing ? /*#__PURE__*/React.createElement("span", {
      className: "iwx-hint"
    }, area === "brush" ? this.state.strokes.length ? "Only the painted area changes." : "Paint over the area to change." : rect ? "Only what is inside the box changes. Drag again to redraw." : "Drag a box around the area to change.") : null, tool === "crop" ? /*#__PURE__*/React.createElement("span", {
      className: "iwx-hint"
    }, rect ? "What is inside the box becomes the new version." : "Drag a box to crop.") : null, busy ? /*#__PURE__*/React.createElement("span", {
      className: "iwx-busy"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "autorenew"), busy) : null)), !compare ? /*#__PURE__*/React.createElement("div", {
      className: "iwx-prompt" + (drawing ? " focus" : "")
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons spark"
    }, "auto_awesome"), /*#__PURE__*/React.createElement("label", {
      className: "in"
    }, /*#__PURE__*/React.createElement("span", {
      className: "sr"
    }, "Describe a change"), /*#__PURE__*/React.createElement("input", {
      value: this.state.rtPrompt,
      disabled: !!busy || drawing && this.state.rtErase,
      placeholder: "Describe a change, e.g. remove the price tag",
      onChange: e => this.setState({
        rtPrompt: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter" && this.canApply("retouch")) this.apply("retouch");
      }
    })), /*#__PURE__*/React.createElement("span", {
      className: "iwx-pact"
    }, /*#__PURE__*/React.createElement("span", {
      className: "iwx-seg",
      role: "group",
      "aria-label": "Where the change applies"
    }, [["whole", "Whole image"], ["box", "Box"], ["brush", "Brush"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: (tool === "retouch" ? area : "whole") === k ? "on" : "",
      disabled: !!busy,
      onClick: () => this.setArea(k)
    }, l))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iwx-go",
      disabled: !this.canApply("retouch") || drawing && this.state.rtErase,
      onClick: () => this.apply("retouch")
    }, busy ? "Working…" : "Generate"))) : null, drawing && !this.state.rtErase ? /*#__PURE__*/React.createElement("div", {
      className: "iwx-try"
    }, /*#__PURE__*/React.createElement("span", null, "Try:"), ["Remove it", "Replace it with ", "Change its color to ", "Sharpen it"].map(t => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: t,
      onClick: () => this.setState({
        rtPrompt: t
      })
    }, t.trim(), / $/.test(t) ? "…" : ""))) : null)), this.renderFilm(cur)), this.renderSide(cur, placements)), approveOpen ? /*#__PURE__*/React.createElement(ApproveExportSheet, {
      family: family,
      version: cur,
      placements: placements,
      lang: lang,
      langs: this.langs(cur),
      base: this.baseLang(cur),
      shopify: this.state.shopify,
      history: this.props.history,
      onClose: () => this.setState({
        approveOpen: false
      }),
      onReload: sel => this.loadFamily(family.id, sel),
      onCredits: c => this.setState({
        credits: c
      }),
      onPushed: this.onPushed
    }) : null, this.state.colorwayOpen ? /*#__PURE__*/React.createElement(ColorwaySheet, {
      family: family,
      version: cur,
      options: this.state.options,
      placements: placements,
      lang: lang,
      initial: this.state.colorwayInit,
      onClose: () => this.setState({
        colorwayOpen: false,
        colorwayInit: null
      }),
      onReload: sel => this.loadFamily(family.id, sel)
    }) : null);
  }
  /** Versions as a filmstrip under the stage: click one to keep editing from it. */
  renderFilm(cur) {
    const {
      family,
      busy
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "iwx-film"
    }, /*#__PURE__*/React.createElement("span", {
      className: "iwx-eyebrow"
    }, "Versions"), /*#__PURE__*/React.createElement("div", {
      className: "iwx-strip"
    }, family.versions.map((v, i) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: v.id
    }, i > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "material-icons arrow"
    }, "chevron_right") : null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iwx-ver" + (v.id === cur.id ? " sel" : ""),
      disabled: !!busy,
      title: `v${v.version} · ${opLabel(v)}${this.branchFrom(v) ? ` · ${this.branchFrom(v)}` : ""}`,
      onClick: () => this.select(v)
    }, /*#__PURE__*/React.createElement("img", {
      src: v.url,
      alt: "",
      loading: "lazy"
    }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "v", v.version), " ", !v.parentVersionId && v.version <= 1 ? "Original" : opLabel(v))))), this.state.pendingJobs > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons arrow"
    }, "chevron_right"), /*#__PURE__*/React.createElement("span", {
      className: "iwx-ver pending"
    }, /*#__PURE__*/React.createElement("i", null, "Working\u2026"), /*#__PURE__*/React.createElement("span", null, "new version"))) : null), /*#__PURE__*/React.createElement("span", {
      className: "iwx-filmhint"
    }, "Click any version to keep editing from it. Nothing is ever overwritten."));
  }
  renderSide(cur, placements) {
    const s = this.state;
    const tab = s.sideTab || "edit";
    const exportN = (cur.placements || []).length;
    return /*#__PURE__*/React.createElement("div", {
      className: "iwx-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "iwx-tabs",
      role: "tablist"
    }, [["edit", "Edit"], ["sizes", "Sizes"], ["details", "Details"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      role: "tab",
      "aria-selected": tab === k,
      className: tab === k ? "on" : "",
      onClick: () => this.setState({
        sideTab: k,
        previewKey: k === "sizes" ? s.previewKey : null
      })
    }, l, k === "sizes" && exportN ? /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, exportN) : null))), /*#__PURE__*/React.createElement("div", {
      className: "iwx-pane"
    }, tab === "edit" ? this.renderTools(cur) : tab === "sizes" ? this.renderSizes(cur, placements) : this.renderDetails(cur)));
  }
  renderTools(cur) {
    const s = this.state;
    const busy = !!s.busy;
    const tool = s.tool;
    if (tool && TOOL_INFO[tool]) {
      const [name, desc] = TOOL_INFO[tool];
      return /*#__PURE__*/React.createElement("div", {
        className: "iwx-tool"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "iwx-back",
        onClick: () => this.setState({
          tool: "",
          rect: null,
          strokes: [],
          area: "whole",
          rtErase: false
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "chevron_left"), "All tools"), /*#__PURE__*/React.createElement("h3", null, name), /*#__PURE__*/React.createElement("p", null, desc), this.renderToolForm(cur, this.state.family.category));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, TILE_GROUPS.map(([g, keys]) => /*#__PURE__*/React.createElement("div", {
      key: g,
      className: "iw-tilegroup"
    }, /*#__PURE__*/React.createElement("span", {
      className: "iwx-eyebrow"
    }, g), /*#__PURE__*/React.createElement("div", {
      className: "iw-tiles"
    }, keys.map(k => {
      const t = TILES.find(x => x[0] === k);
      if (!t) return null;
      const [, icon, l] = t;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: k,
        className: "hub-edit-tile",
        disabled: busy,
        title: TOOL_INFO[k] ? TOOL_INFO[k][1] : l,
        onClick: () => this.pickTool(k)
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, icon), /*#__PURE__*/React.createElement("b", null, l));
    })))), /*#__PURE__*/React.createElement("div", {
      className: "iwx-check"
    }, fidelityChip(cur.fidelity, cur.aiGenerated, true) || /*#__PURE__*/React.createElement("span", {
      className: "plk-chip"
    }, "Product check"), /*#__PURE__*/React.createElement("span", null, cur.fidelity ? cur.fidelity.note || "Checked against the product." : noCheckReason(cur))));
  }
  renderSizes(cur, placements) {
    const {
      busy,
      lang,
      previewKey
    } = this.state;
    const base = this.baseLang(cur);
    return /*#__PURE__*/React.createElement("div", {
      className: "iwx-sizes"
    }, /*#__PURE__*/React.createElement("p", {
      className: "iwx-lead"
    }, "Pick a size to see it large. Switch on the ones Approve and export should make."), this.state.registryMissing ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "The size list is not available yet: showing the standard set.") : null, placements.map(p => {
      const on = (cur.placements || []).indexOf(p.key) !== -1;
      const fits = api.placementFit(cur, p).fits;
      const a = api.aspectNum(p.aspect);
      return /*#__PURE__*/React.createElement("div", {
        key: p.key,
        className: "iwx-size" + (previewKey === p.key ? " sel" : "")
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "pick",
        onClick: () => this.previewPlacement(p.key),
        title: `See the ${p.name} crop large`
      }, /*#__PURE__*/React.createElement("span", {
        className: "th"
      }, /*#__PURE__*/React.createElement(PlacementFrame, {
        p: p,
        version: cur,
        lang: lang,
        base: base,
        scale: 0.3,
        style: a >= 1 ? {
          width: "44px"
        } : {
          height: "44px"
        }
      })), /*#__PURE__*/React.createElement("span", {
        className: "t"
      }, /*#__PURE__*/React.createElement("b", null, p.name), /*#__PURE__*/React.createElement("span", null, p.width, " \xD7 ", p.height, " \xB7 ", /*#__PURE__*/React.createElement("i", {
        className: fits ? "ok" : "warn"
      }, fits ? "fits" : "needs more room")))), /*#__PURE__*/React.createElement("label", {
        className: "sw",
        title: "Make this size when you approve and export"
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: on,
        disabled: !!busy,
        onChange: e => this.togglePlacement(p.key, e.target.checked)
      }), /*#__PURE__*/React.createElement("span", {
        className: "sr"
      }, "Export ", p.name)));
    }));
  }
  renderDetails(cur) {
    const s = this.state;
    const family = s.family;
    const root = family.root || family.versions[0];
    const product = family.product;
    const cat = family.category;
    return /*#__PURE__*/React.createElement("div", {
      className: "iwx-details"
    }, /*#__PURE__*/React.createElement("div", {
      className: "iw-card"
    }, /*#__PURE__*/React.createElement("h6", null, "This version"), /*#__PURE__*/React.createElement("div", {
      className: "kv"
    }, /*#__PURE__*/React.createElement("span", null, "Made by"), /*#__PURE__*/React.createElement("div", null, cur.aiGenerated ? "Plunk Studio AI" : root && root.id === cur.id ? sourceLabel(root.source) || "Original" : "A simple edit (no AI)"), cur.parentVersionId ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "From"), /*#__PURE__*/React.createElement("div", null, "v", (family.versions.find(v => v.id === cur.parentVersionId) || {}).version || "?")) : null, /*#__PURE__*/React.createElement("span", null, "Change"), /*#__PURE__*/React.createElement("div", null, opLabel(cur)), cur.createdBy ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "By"), /*#__PURE__*/React.createElement("div", null, userShort(cur.createdBy), cur.createdDate ? ` · ${timeAgo(cur.createdDate)}` : "")) : null)), /*#__PURE__*/React.createElement("div", {
      className: "iw-card"
    }, /*#__PURE__*/React.createElement("h6", null, "Product"), /*#__PURE__*/React.createElement("div", {
      className: "kv"
    }, /*#__PURE__*/React.createElement("span", null, "Product"), /*#__PURE__*/React.createElement("div", null, product ? /*#__PURE__*/React.createElement(React.Fragment, null, product.title || "Product", " ", product.source ? /*#__PURE__*/React.createElement("span", {
      className: "plk-chip"
    }, sourceLabel(product.source)) : null) : /*#__PURE__*/React.createElement(React.Fragment, null, "None ", /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "(any image works; colors are marked for review)"))), /*#__PURE__*/React.createElement("span", null, "Category"), /*#__PURE__*/React.createElement("div", null, cat ? cat.label : "General"), /*#__PURE__*/React.createElement("span", null, "Product check"), /*#__PURE__*/React.createElement("div", null, fidelityChip(cur.fidelity, cur.aiGenerated) || /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, noCheckReason(cur))))), /*#__PURE__*/React.createElement(ShopifyBlock, {
      shopify: s.shopify,
      family: family
    }), /*#__PURE__*/React.createElement(StatsStrip, {
      stats: s.stats
    }), /*#__PURE__*/React.createElement(SimilarStrip, {
      items: s.similar,
      onOpen: x => this.props.history.push(`/Content/images/${x.rootId || x.id}`)
    }));
  }
  renderToolForm(cur, cat) {
    const s = this.state;
    const busy = !!s.busy;
    const tool = s.tool;
    const people = this.peopleOn();
    const scenes = cat && cat.scenes || api.FALLBACK_RECIPES[9].scenes;
    const optionKeys = Object.keys(s.options || {});
    const values = (s.options || {})[s.rcAttr] || [];
    const scenesOff = k => !people && PEOPLE_KEYS.has(k);
    return /*#__PURE__*/React.createElement(React.Fragment, null, tool === "background" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement(Row, {
      k: "Mode"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [["white", "White"], ["transparent", "Transparent"], ["brand", "Brand"], ["hex", "Color"], ["scene", "Scene"]],
      value: s.bgMode,
      disabled: busy,
      onChange: k => this.setState({
        bgMode: k
      })
    })), s.bgMode === "hex" ? /*#__PURE__*/React.createElement("div", {
      className: "ec-field iw-color"
    }, /*#__PURE__*/React.createElement("label", null, "Color"), /*#__PURE__*/React.createElement("span", {
      className: "iw-color-row"
    }, /*#__PURE__*/React.createElement("input", {
      type: "color",
      value: s.bgHex,
      onChange: e => this.setState({
        bgHex: e.target.value
      })
    }), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: s.bgHex,
      maxLength: 7,
      onChange: e => this.setState({
        bgHex: e.target.value
      })
    }))) : null, s.bgMode === "scene" ? /*#__PURE__*/React.createElement(Row, {
      k: "Scene"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: scenes.map(k => [k, nice(k)]),
      value: s.bgScene || scenes.find(k => !scenesOff(k)) || "",
      disabled: busy,
      off: scenesOff,
      onChange: k => this.setState({
        bgScene: k
      })
    })) : null, /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, s.bgMode === "scene" ? "Scenes come from the category recipe. The product never moves or rescales." : "Transparent keeps a PNG with alpha. The product stays exactly as it is."), this.applyRow()), tool === "extend" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement(Row, {
      k: "To"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: EXTEND_ASPECTS,
      value: s.exAspect,
      disabled: busy,
      onChange: k => this.setState({
        exAspect: k
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Hint ", /*#__PURE__*/React.createElement("i", null, "optional")), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: s.exHint,
      placeholder: "e.g. more surface and soft shadow",
      disabled: busy,
      onChange: e => this.setState({
        exHint: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "The scene continues at the edges; the original pixels stay. A crop is used when it already satisfies the safe zone."), this.applyRow()), tool === "retouch" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement("ol", {
      className: "iwx-steps"
    }, /*#__PURE__*/React.createElement("li", null, "Pick ", /*#__PURE__*/React.createElement("b", null, "Box"), " or ", /*#__PURE__*/React.createElement("b", null, "Brush"), " in the bar under the image"), /*#__PURE__*/React.createElement("li", null, s.area === "brush" ? "Paint over" : "Drag a box around", " the area on the image"), /*#__PURE__*/React.createElement("li", null, "Describe the change and press ", /*#__PURE__*/React.createElement("b", null, "Generate"))), s.area === "brush" ? /*#__PURE__*/React.createElement(Row, {
      k: "Brush"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: BRUSH_SIZES,
      value: s.brushSize,
      disabled: busy,
      onChange: k => this.setState({
        brushSize: k
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "iw-seg"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      disabled: busy || !s.strokes.length,
      onClick: () => this.setState({
        strokes: s.strokes.slice(0, -1)
      })
    }, "Undo"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      disabled: busy || !s.strokes.length,
      onClick: () => this.setState({
        strokes: []
      })
    }, "Clear"))) : null, /*#__PURE__*/React.createElement(Check, {
      checked: s.rtErase,
      disabled: busy,
      onChange: v => this.setState({
        rtErase: v
      })
    }, "Just remove what is marked (the surroundings fill in)"), s.rtErase ? this.applyRow("Remove") : null), tool === "recolor" && (() => {
      const picks = this.colorPicks();
      return /*#__PURE__*/React.createElement("div", {
        className: "ec-form iw-form"
      }, optionKeys.length > 1 ? /*#__PURE__*/React.createElement(Row, {
        k: "What changes"
      }, /*#__PURE__*/React.createElement(Seg, {
        list: optionKeys.map(k => [k, k]),
        value: s.rcAttr,
        disabled: busy,
        onChange: k => this.setState({
          rcAttr: k,
          rcPicks: []
        })
      })) : null, optionKeys.length ? /*#__PURE__*/React.createElement("div", {
        className: "ec-field"
      }, /*#__PURE__*/React.createElement("label", null, s.rcAttr || "Options", " to make ", /*#__PURE__*/React.createElement("i", null, "from the product's options \xB7 ", picks.length, " picked")), /*#__PURE__*/React.createElement("div", {
        className: "iwx-swatches"
      }, values.map(v => {
        const val = String(v);
        const on = s.rcPicks.indexOf(val) !== -1;
        const dot = typeof CSS !== "undefined" && CSS.supports && CSS.supports("color", val.toLowerCase()) ? val.toLowerCase() : null;
        return /*#__PURE__*/React.createElement("button", {
          type: "button",
          key: val,
          className: "iwx-swatch" + (on ? " on" : ""),
          disabled: busy,
          onClick: () => this.setState({
            rcPicks: on ? s.rcPicks.filter(x => x !== val) : [...s.rcPicks, val]
          })
        }, /*#__PURE__*/React.createElement("span", {
          className: "dot",
          style: dot ? {
            background: dot
          } : undefined
        }), val);
      }))) : /*#__PURE__*/React.createElement("div", {
        className: "ec-field"
      }, /*#__PURE__*/React.createElement("label", null, "Colors to make ", /*#__PURE__*/React.createElement("i", null, "separate with commas")), /*#__PURE__*/React.createElement("input", {
        className: "ec-input",
        value: s.rcFree,
        placeholder: "e.g. Navy, Sage, Charcoal",
        disabled: busy,
        onChange: e => this.setState({
          rcFree: e.target.value
        })
      })), /*#__PURE__*/React.createElement("div", {
        className: "ec-help"
      }, optionKeys.length ? "Each new version is checked against the product so its shape and details stay true." : "No color options on file for this image, so the results are marked for review.", picks.length > 1 ? " You approve the set together." : ""), /*#__PURE__*/React.createElement("div", {
        className: "iw-apply"
      }, /*#__PURE__*/React.createElement("span", {
        className: "territory-hint"
      }, picks.length > 1 ? `${picks.length} new versions from v${cur.version}` : picks.length === 1 ? `${picks[0]} · saved as v${this.state.family.versions.length + 1}` : optionKeys.length ? "Pick at least one" : "Type at least one color"), /*#__PURE__*/React.createElement("span", {
        className: "sp"
      }), /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: busy || !picks.length,
        onClick: this.makeColors
      }, s.busy ? "Working…" : picks.length > 1 ? `Make ${picks.length} versions` : "Apply")));
    })(), tool === "straighten" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Degrees ", /*#__PURE__*/React.createElement("i", null, s.stDeg > 0 ? "clockwise" : s.stDeg < 0 ? "counter-clockwise" : "level")), /*#__PURE__*/React.createElement("span", {
      className: "iw-range"
    }, /*#__PURE__*/React.createElement("input", {
      type: "range",
      min: -15,
      max: 15,
      step: 0.5,
      value: s.stDeg,
      disabled: busy,
      onChange: e => this.setState({
        stDeg: Number(e.target.value)
      })
    }), /*#__PURE__*/React.createElement("input", {
      className: "ec-input ec-num",
      type: "number",
      min: -15,
      max: 15,
      step: 0.5,
      value: s.stDeg,
      disabled: busy,
      onChange: e => this.setState({
        stDeg: Math.max(-15, Math.min(15, Number(e.target.value) || 0))
      })
    }))), /*#__PURE__*/React.createElement(Check, {
      checked: s.stCrop,
      disabled: busy,
      onChange: v => this.setState({
        stCrop: v
      })
    }, "Auto-crop the empty corners"), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Free. Auto-detect of the horizon is a later increment."), this.applyRow()), tool === "crop" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Draw the box on the canvas; what is inside becomes the new version. Free."), this.applyRow()), tool === "flip" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement(Row, {
      k: "Direction"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [[true, "Horizontal"], [false, "Vertical"]],
      value: s.flipH,
      disabled: busy,
      onChange: k => this.setState({
        flipH: k
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Mirror the image. Free. Text and logos in the photo mirror too."), this.applyRow()), tool === "rotate" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement(Row, {
      k: "Turn"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [[90, "90° right"], [180, "180°"], [270, "90° left"]],
      value: s.rotDeg,
      disabled: busy,
      onChange: k => this.setState({
        rotDeg: k
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Quarter turns. Free. Use Straighten for small angles."), this.applyRow()), tool === "upscale" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Doubles the pixel size with a sharp resample, for product-page zoom. Free. Hi-res on export stays the AI finalize."), this.applyRow()), tool === "packshot" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement(Row, {
      k: "Background"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [["white", "White"], ["brand", "Brand accent"], ["hex", "Color"]],
      value: s.psBg,
      disabled: busy,
      onChange: k => this.setState({
        psBg: k
      })
    })), s.psBg === "hex" ? /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Color"), /*#__PURE__*/React.createElement("input", {
      className: "ec-input ec-num",
      type: "color",
      value: s.psHex,
      disabled: busy,
      onChange: e => this.setState({
        psHex: e.target.value
      })
    })) : null, /*#__PURE__*/React.createElement(Row, {
      k: "Padding"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [[4, "Tight"], [8, "Standard"], [14, "Airy"]],
      value: s.psPad,
      disabled: busy,
      onChange: k => this.setState({
        psPad: k
      })
    })), /*#__PURE__*/React.createElement(Check, {
      checked: s.psShadow,
      disabled: busy,
      onChange: v => this.setState({
        psShadow: v
      })
    }, "Soft contact shadow"), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Cuts the product out and centers it on a clean background. The cutout is an AI step."), this.applyRow()), tool === "logo" && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form"
    }, /*#__PURE__*/React.createElement(Row, {
      k: "Corner"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [["top_left", "Top left"], ["top_right", "Top right"], ["bottom_left", "Bottom left"], ["bottom_right", "Bottom right"]],
      value: s.lgPos,
      disabled: busy,
      onChange: k => this.setState({
        lgPos: k
      })
    })), /*#__PURE__*/React.createElement(Row, {
      k: "Size"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [[12, "Small"], [18, "Medium"], [26, "Large"]],
      value: s.lgScale,
      disabled: busy,
      onChange: k => this.setState({
        lgScale: k
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "The brand kit logo (Manage Data \u203A Brand kit), placed with a safe margin. Free."), this.applyRow()), tool === "text" && (() => {
      const base = this.baseLang(cur);
      const langs = this.langs(cur);
      const missing = this.missingLangs(cur);
      const tb = s.textBusy;
      return /*#__PURE__*/React.createElement("div", {
        className: "ec-form iw-form"
      }, /*#__PURE__*/React.createElement(Row, {
        k: "Languages"
      }, /*#__PURE__*/React.createElement(LanguageChips, {
        base: base,
        langs: langs,
        value: s.lang,
        busy: busy ? "busy" : tb,
        missing: missing,
        onPick: k => this.setState({
          lang: k
        }),
        onAdd: k => this.setState({
          addedLangs: [...s.addedLangs, k],
          lang: k
        }),
        onRemove: k => {
          const text = {
            headline: {
              ...s.text.headline
            },
            sub: {
              ...s.text.sub
            },
            cta: {
              ...s.text.cta
            }
          };
          delete text.headline[k];
          delete text.sub[k];
          delete text.cta[k];
          this.setState({
            addedLangs: s.addedLangs.filter(x => x !== k),
            lang: s.lang === k ? base : s.lang,
            text,
            saved: "Saving…"
          }, () => this.debounce("text", this.saveText, 300));
        },
        onTranslate: this.translate
      })), /*#__PURE__*/React.createElement("div", {
        className: "ec-field"
      }, /*#__PURE__*/React.createElement("label", null, "Headline ", /*#__PURE__*/React.createElement("i", null, "layer \xB7 ", api.langName(s.lang), s.lang === base ? " (base)" : "")), /*#__PURE__*/React.createElement("input", {
        className: "ec-input",
        value: s.text.headline[s.lang] || "",
        disabled: !!tb,
        placeholder: s.lang !== base && s.text.headline[base] ? `${base.toUpperCase()}: ${s.text.headline[base]}` : "e.g. New: Vitamin C Serum",
        onChange: e => this.setText("headline", e.target.value)
      })), /*#__PURE__*/React.createElement("div", {
        className: "ec-field"
      }, /*#__PURE__*/React.createElement("label", null, "Sub ", /*#__PURE__*/React.createElement("i", null, "optional")), /*#__PURE__*/React.createElement("input", {
        className: "ec-input",
        value: s.text.sub[s.lang] || "",
        disabled: !!tb,
        placeholder: s.lang !== base && s.text.sub[base] ? `${base.toUpperCase()}: ${s.text.sub[base]}` : "One line under the headline",
        onChange: e => this.setText("sub", e.target.value)
      })), /*#__PURE__*/React.createElement("div", {
        className: "ec-field"
      }, /*#__PURE__*/React.createElement("label", null, "Call to action"), /*#__PURE__*/React.createElement("input", {
        className: "ec-input",
        value: s.text.cta[s.lang] || "",
        disabled: !!tb,
        placeholder: s.lang !== base && s.text.cta[base] ? `${base.toUpperCase()}: ${s.text.cta[base]}` : "e.g. Shop now",
        onChange: e => this.setText("cta", e.target.value)
      })), /*#__PURE__*/React.createElement("div", {
        className: "iw-grid-2"
      }, /*#__PURE__*/React.createElement(FontsSection, {
        fonts: s.fonts === undefined ? null : s.fonts,
        value: s.font,
        disabled: busy,
        onChange: (font, row) => this.setState({
          font,
          fontId: row ? row.id : null,
          saved: "Saving…"
        }, () => this.debounce("text", this.saveText, 400)),
        onFontsChange: fonts => this.setState({
          fonts
        })
      }), /*#__PURE__*/React.createElement("div", {
        className: "ec-field"
      }, /*#__PURE__*/React.createElement("label", null, "Color"), /*#__PURE__*/React.createElement("select", {
        value: s.color,
        onChange: e => this.setState({
          color: e.target.value,
          saved: "Saving…"
        }, () => this.debounce("text", this.saveText, 400))
      }, COLORS.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
        key: k,
        value: k
      }, l))))), /*#__PURE__*/React.createElement("div", {
        className: "ec-help"
      }, "Drawn live, rendered into pixels only at export. Omitted on channels that do not allow text in the image.", missing.length ? ` ${missing.map(k => k.toUpperCase()).join(" and ")} empty: Translate fills them, or type each; exports in those languages fall back to ${base.toUpperCase()} with a warning.` : ""));
    })(), (tool === "text" || tool === "badge") && /*#__PURE__*/React.createElement("div", {
      className: "ec-form iw-form iwx-subsection"
    }, /*#__PURE__*/React.createElement("h6", {
      className: "iwx-sub"
    }, "Badge ", /*#__PURE__*/React.createElement("i", null, "burned into the image")), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Text ", /*#__PURE__*/React.createElement("i", null, "up to 24 characters")), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: s.bdText,
      maxLength: 24,
      disabled: busy,
      placeholder: "Sale",
      onChange: e => this.setState({
        bdText: e.target.value
      })
    })), /*#__PURE__*/React.createElement(Row, {
      k: "Corner"
    }, /*#__PURE__*/React.createElement(Seg, {
      list: [["top_left", "Top left"], ["top_right", "Top right"], ["bottom_left", "Bottom left"], ["bottom_right", "Bottom right"]],
      value: s.bdPos,
      disabled: busy,
      onChange: k => this.setState({
        bdPos: k
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "A pill in the brand accent, burned into the pixels. Free. Channels that forbid text in the image will still show it, so tick placements with care."), this.applyRow(null, "badge")));
  }
  applyRow(label, forced) {
    const cur = this.current();
    const t = TILES.find(x => x[0] === (forced || this.state.tool));
    return /*#__PURE__*/React.createElement("div", {
      className: "iw-apply"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, label || (t ? t[2] : ""), " on v", cur.version, " \xB7 saved as v", this.state.family.versions.length + 1), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !this.canApply(forced),
      onClick: () => this.apply(forced)
    }, this.state.busy ? "Working…" : "Apply"));
  }
  /* ---------- 6 · later tiles ---------- */
}

/* =================================================================== 4 · Approve and Export */
class ApproveExportSheet extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      estimate: undefined,
      busy: "",
      err: "",
      exports: null,
      credits: null,
      captions: {},
      gate: "",
      exportLangs: null,
      shopifyOpen: false
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "toggleLang", k => {
      const cur = this.exportLangs();
      const next = cur.indexOf(k) !== -1 ? cur.filter(x => x !== k) : [...cur, k];
      this.setState({
        exportLangs: next.length ? next : [this.props.base || "en"]
      });
    });
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape" && !this.state.busy && !this.state.shopifyOpen) this.props.onClose();
    });
    _defineProperty(this, "load", async () => {
      const {
        family,
        version
      } = this.props;
      const [est, ex] = await Promise.all([api.getEstimate(family.id, {
        versionId: version.id,
        placements: version.placements.join(",")
      }), api.getExports(family.id, version.id)]);
      if (!this._alive) return;
      const captions = {};
      (ex || []).forEach(x => {
        if (x && x.placementKey) captions[x.placementKey] = {
          caption: x.caption || "",
          altText: x.altText || ""
        };
      });
      this.setState({
        estimate: est || null,
        exports: ex && ex.length ? ex : null,
        captions: {
          ...captions,
          ...this.state.captions
        },
        credits: est && est.insufficientCredits ? {
          insufficient: true,
          buyUrl: est.buyUrl
        } : null
      });
    });
    _defineProperty(this, "setCap", (k, patch) => this.setState({
      captions: {
        ...this.state.captions,
        [k]: {
          ...(this.state.captions[k] || {
            caption: "",
            altText: this.props.version.altText || ""
          }),
          ...patch
        }
      }
    }));
    _defineProperty(this, "approveAs", async state => {
      const {
        family,
        version
      } = this.props;
      this.setState({
        busy: "approve",
        err: ""
      });
      try {
        await api.approve(family.id, version.id, state);
        toast(state === "approved" ? `v${version.version} approved.` : state === "in_review" ? "Review requested." : "Back to draft.");
        await this.props.onReload(version.id);
      } catch (e) {
        if (e && e.status === 403) this.setState({
          err: "Only an administrator or owner can approve."
        });else if (api.isSoft(e)) this.setState({
          err: "Approval is not available yet."
        });else if (e && e.data && e.data.code === "fidelity_failed") this.setState({
          err: "The fidelity check failed on this version: fix it or make a new version before approving."
        });else this.setState({
          err: e && e.message || "Could not change the state."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
    _defineProperty(this, "run", async allowDraft => {
      const {
        family,
        version,
        lang
      } = this.props;
      const keys = this.ticked().map(p => p.key);
      if (!keys.length) {
        this.setState({
          err: "Tick at least one placement in the rail first."
        });
        return;
      }
      this.setState({
        busy: "export",
        err: "",
        gate: "",
        credits: null
      });
      try {
        const est = this.state.estimate;
        if (!allowDraft && est && (est.needsHires ?? est.hiresNeeded) && !version.hasHires) {
          this.setState({
            busy: "hires"
          });
          const r = await api.makeHires(family.id, version.id);
          const jobId = r && (r.jobId || r.id);
          if (jobId) {
            const done = await api.waitJob(jobId, () => this._alive);
            if (done.status === "failed") throw new Error(done.error || "The hi-res render failed.");
            if (done.status === "timeout") {
              this.setState({
                err: "The hi-res render is still running: the exports appear when it finishes."
              });
              return;
            }
          }
          this.setState({
            busy: "export"
          });
        }
        const languages = this.exportLangs();
        const r = await api.exportImage(family.id, {
          versionId: version.id,
          placements: keys,
          language: languages.indexOf(lang) !== -1 ? lang : languages[0],
          languages,
          allowDraft: !!allowDraft,
          captions: this.state.captions
        });
        let exports = r && r.exports;
        const jobId = r && (r.jobId || r.batchId);
        if ((!exports || !exports.length) && jobId) {
          const done = await api.waitJob(jobId, () => this._alive);
          if (done.status === "failed") throw new Error(done.error || "The export failed.");
          if (done.status === "timeout") {
            this.setState({
              err: "The export is still running: it appears under Exports when it finishes."
            });
            return;
          }
          exports = await api.getExports(family.id, version.id);
        }
        if (!this._alive) return;
        this.setState({
          exports: exports || []
        });
        toast(`${(exports || []).length} export${(exports || []).length === 1 ? "" : "s"} ready.`);
        this.props.onReload(version.id);
      } catch (e) {
        if (!this._alive) return;
        const code = e && e.data && (e.data.code || e.data.error);
        if (e && (e.status === 402 || code === "insufficient_credits" || code === "out_of_credits")) this.setState({
          credits: {
            insufficient: true,
            buyUrl: e.data && e.data.buyUrl || "https://account.plunk.io/?from=app"
          }
        });else if (e && e.status === 409 && code === "approval_required") this.setState({
          gate: "This workspace requires approval before hi-res and export. Export with the DRAFT mark to share a real crop now."
        });else if (api.isSoft(e)) this.setState({
          err: "Export is not available yet."
        });else this.setState({
          err: e && e.message || "Could not export."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
  }
  componentDidMount() {
    document.addEventListener("keydown", this.onKey);
    this.load();
  }
  /** Languages to export: the user's picks, else the base language plus every language that has text on the layers. */
  exportLangs() {
    if (this.state.exportLangs) return this.state.exportLangs;
    const {
      version,
      base
    } = this.props;
    const b = base || "en";
    const withText = (this.props.langs || [b]).filter(k => k === b || (version.textLayers || []).some(l => l && l.text && l.text[k]));
    return withText.length ? withText : [b];
  }
  componentWillUnmount() {
    this._alive = false;
    document.removeEventListener("keydown", this.onKey);
  }
  ticked() {
    return this.props.placements.filter(p => this.props.version.placements.indexOf(p.key) !== -1);
  }
  render() {
    const {
      family,
      version,
      lang,
      onClose,
      base,
      langs
    } = this.props;
    const {
      estimate,
      busy,
      err,
      exports,
      credits,
      captions,
      gate,
      shopifyOpen
    } = this.state;
    const b = base || "en";
    const allLangs = langs && langs.length ? langs : [b];
    const exportLangs = this.exportLangs();
    const multi = exportLangs.length > 1;
    const doneFor = (key, lg) => (exports || []).find(x => x.placementKey === key && (String(x.language || b).toLowerCase() === lg || !x.language && lg === b));
    const ticked = this.ticked();
    const stateIdx = Math.max(0, APPROVAL_STEPS.findIndex(([k]) => k === (version.approvalState || "draft")));
    const canApprove = family.approval && family.approval.canApprove != null ? !!family.approval.canApprove : api.isAdministrator();
    const approved = version.approvalState === "approved" || version.approvalState === "published";
    const required = !!(family.approval && family.approval.required);
    const chain = (() => {
      const out = [];
      let p = version;
      const seen = new Set();
      while (p && !seen.has(p.id)) {
        seen.add(p.id);
        out.push(p);
        p = p.parentVersionId ? family.versions.find(x => x.id === p.parentVersionId) : null;
      }
      return out;
    })();
    const prompts = chain.map(v => v.prompt).filter(Boolean);
    const refs = (version.references || []).map(r => r.role ? `${r.role} ${r.assetId ? "#" + r.assetId : ""}`.trim() : r.assetId ? `#${r.assetId}` : "").filter(Boolean);
    const missing = allLangs.filter(k => k !== b && !(version.textLayers || []).some(l => l && l.text && l.text[k])).map(k => k.toUpperCase());
    const hasText = (version.textLayers || []).some(l => l && l.text && (l.text[b] || l.text.en));
    const published = version.approvalState === "published";
    const needsHires = estimate ? !!(estimate.needsHires ?? estimate.hiresNeeded) && !version.hasHires : !!version.aiGenerated && !version.hasHires;
    const stop = e => e.stopPropagation();
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop",
      onClick: () => {
        if (!busy) onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "iw-sheet wide ec-form",
      onClick: stop
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tplp-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
      className: "hub-h3",
      style: {
        margin: 0
      }
    }, "Approve and export \xB7 v", version.version), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, family.root && family.root.title || family.product && family.product.title || "Image", " \xB7 ", ticked.length, " placement", ticked.length === 1 ? "" : "s", " ticked")), /*#__PURE__*/React.createElement("span", {
      className: "hub-tplp-acts"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      disabled: !!busy,
      onClick: onClose
    }, "Close"))), /*#__PURE__*/React.createElement("div", {
      className: "iw-ax"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "iw-eyebrow"
    }, "Approval"), /*#__PURE__*/React.createElement("div", {
      className: "iw-steps"
    }, APPROVAL_STEPS.map(([k, l], i) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: k
    }, i > 0 ? /*#__PURE__*/React.createElement("span", {
      className: "arrow"
    }, "\u2192") : null, /*#__PURE__*/React.createElement("span", {
      className: "iw-step" + (i < stateIdx ? " done" : i === stateIdx ? " cur" : "")
    }, /*#__PURE__*/React.createElement("i", null), l)))), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, required ? "This workspace requires approval before Ship, Push to Shopify and hi-res. Approvers: any administrator or owner. " : "Approval is informational for this workspace (turn gating on under Manage Data › Content settings). ", "Exports made before approval carry a DRAFT mark."), /*#__PURE__*/React.createElement("div", {
      className: "ec-inline iw-acts"
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: !canApprove || !!busy || approved,
      title: canApprove ? "" : "Approve is enabled for administrators and owners only",
      onClick: () => this.approveAs("approved")
    }, busy === "approve" ? "Working…" : approved ? "Approved" : "Approve"), version.approvalState === "draft" || !version.approvalState ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      disabled: !!busy,
      onClick: () => this.approveAs("in_review")
    }, "Ask for a review") : null, version.approvalState === "in_review" || approved ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      disabled: !!busy,
      onClick: () => this.approveAs("draft")
    }, "Back to draft") : null, !canApprove ? /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "Approve is enabled for approvers only.") : null), err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err",
      style: {
        marginTop: 6
      }
    }, err) : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-eyebrow",
      style: {
        marginTop: 16
      }
    }, "Exports"), hasText && allLangs.length > 1 ? /*#__PURE__*/React.createElement("div", {
      className: "iw-row",
      style: {
        margin: "8px 0 0"
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Languages"), /*#__PURE__*/React.createElement("span", {
      className: "iw-seg"
    }, allLangs.map(k => {
      const on = exportLangs.indexOf(k) !== -1;
      const empty = k !== b && missing.indexOf(k.toUpperCase()) !== -1;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: k,
        className: "territory-toggle xs" + (on ? " on" : "") + (empty ? " empty" : ""),
        disabled: !!busy,
        title: `${api.langName(k)}${empty ? " · no text on the layer: falls back to " + b.toUpperCase() : ""}`,
        onClick: () => this.toggleLang(k)
      }, k.toUpperCase());
    })), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "every placement \xD7 language in one batch")) : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "iw-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Placement"), /*#__PURE__*/React.createElement("th", null, "Caption"), /*#__PURE__*/React.createElement("th", null, "Alt text"), /*#__PURE__*/React.createElement("th", null, multi ? "By language" : ""))), /*#__PURE__*/React.createElement("tbody", null, ticked.map(p => {
      const c = captions[p.key] || {
        caption: "",
        altText: version.altText || ""
      };
      const noCap = NO_CAPTION.has(p.key) || p.caption === false;
      const chip = (d, lg) => d ? /*#__PURE__*/React.createElement("a", {
        key: lg,
        className: "plk-chip ok",
        href: d.url,
        target: "_blank",
        rel: "noreferrer",
        title: `${lg.toUpperCase()} · ${d.width}x${d.height}${d.watermarked ? " · DRAFT mark" : ""}${(d.warnings || []).length ? " · " + d.warnings.join("; ") : ""}`
      }, multi ? `${lg.toUpperCase()} ` : "", d.watermarked ? "draft" : "ready") : /*#__PURE__*/React.createElement("span", {
        key: lg,
        className: "plk-chip"
      }, multi ? `${lg.toUpperCase()} ` : "", "queued");
      return /*#__PURE__*/React.createElement("tr", {
        key: p.key
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "iw-mini",
        style: {
          aspectRatio: `${api.aspectNum(p.aspect)}`
        }
      }, /*#__PURE__*/React.createElement("img", {
        src: version.url,
        alt: ""
      })), /*#__PURE__*/React.createElement("div", {
        className: "iw-plname"
      }, /*#__PURE__*/React.createElement("b", null, p.name), /*#__PURE__*/React.createElement("span", {
        className: "territory-hint"
      }, p.width, "x", p.height, " \xB7 ", p.format || "jpg", p.textAllowed === false ? " · no text" : hasText ? " · text inside the safe zone" : ""))), /*#__PURE__*/React.createElement("td", null, noCap ? /*#__PURE__*/React.createElement("span", {
        className: "territory-hint"
      }, p.key === "instagram_story" ? "stickers, no caption" : "none on this channel") : /*#__PURE__*/React.createElement("textarea", {
        rows: 2,
        value: c.caption,
        placeholder: "Caption for this channel",
        disabled: !!busy,
        onChange: e => this.setCap(p.key, {
          caption: e.target.value
        })
      })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("textarea", {
        rows: 2,
        value: c.altText,
        placeholder: "What the image shows, for screen readers",
        disabled: !!busy,
        onChange: e => this.setCap(p.key, {
          altText: e.target.value
        })
      })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "iw-langdone"
      }, exportLangs.map(lg => chip(doneFor(p.key, lg), lg)))));
    }), !ticked.length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: 4
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "No placements ticked. Tick them in the rail under the canvas."))) : null, this.props.placements.filter(p => version.placements.indexOf(p.key) === -1).length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: 4
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "Not ticked: ", this.props.placements.filter(p => version.placements.indexOf(p.key) === -1).map(p => p.name).join(" · ")))) : null))), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Alt text is accessibility text. Links from these exports carry ", /*#__PURE__*/React.createElement("code", null, "utm_content=a", version.id), " so the funnel can credit this image.")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "iw-eyebrow"
    }, "Hi-res"), /*#__PURE__*/React.createElement("div", {
      className: "ec-help",
      style: {
        marginTop: 6
      }
    }, version.hasHires ? `The full-resolution file for v${version.version} exists: every crop is a free export of it.` : needsHires ? `v${version.version} has AI steps, so a full-resolution render is made once; then every crop is free.` : `v${version.version} has only mechanical steps: exports use the original pixels, nothing to render.`), credits && credits.insufficient ? /*#__PURE__*/React.createElement("div", {
      className: "iw-notice inset"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for the hi-res render."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(credits.buyUrl || "https://account.plunk.io/?from=app", "_blank", "noopener")
    }, "Buy credits")) : null, gate ? /*#__PURE__*/React.createElement("div", {
      className: "iw-notice inset"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "lock"), /*#__PURE__*/React.createElement("span", null, gate)) : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-card"
    }, /*#__PURE__*/React.createElement("h6", null, "Provenance"), /*#__PURE__*/React.createElement("div", {
      className: "kv"
    }, /*#__PURE__*/React.createElement("span", null, "Made with"), /*#__PURE__*/React.createElement("div", null, version.aiGenerated ? /*#__PURE__*/React.createElement(React.Fragment, null, "Plunk Studio AI ", /*#__PURE__*/React.createElement("span", {
      className: "plk-chip accent"
    }, "AI-made")) : family.root && family.root.id === version.id ? sourceLabel(family.root.source) || "Upload" : "Mechanical edit"), /*#__PURE__*/React.createElement("span", null, "From"), /*#__PURE__*/React.createElement("div", null, chain.map((v, i) => `v${v.version}${i === 0 ? "" : ` (${opLabel(v).toLowerCase()})`}`).join(" ← ") || `v${version.version}`), prompts.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, "Prompt"), /*#__PURE__*/React.createElement("div", {
      className: "territory-hint"
    }, prompts.map(p => `"${String(p).slice(0, 90)}${String(p).length > 90 ? "…" : ""}"`).join(" · "))) : null, /*#__PURE__*/React.createElement("span", null, "References"), /*#__PURE__*/React.createElement("div", null, refs.length ? refs.join(", ") : family.product ? "v1 product photo" : "none"), /*#__PURE__*/React.createElement("span", null, "Author"), /*#__PURE__*/React.createElement("div", null, version.createdBy || api.currentUser() || "—"), /*#__PURE__*/React.createElement("span", null, "Rights"), /*#__PURE__*/React.createElement("div", null, nice(version.rights || (version.aiGenerated ? "generated" : family.root && family.root.rights || "unknown")), family.root && family.root.rights && version.rights !== family.root.rights ? /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, " (root: ", nice(family.root.rights), ")") : null), /*#__PURE__*/React.createElement("span", null, "Language"), /*#__PURE__*/React.createElement("div", null, (lang || version.language || "en").toUpperCase(), hasText && missing.length ? /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, " \xB7 ", missing.join(", "), " missing on the text layer") : null), /*#__PURE__*/React.createElement("span", null, "Category"), /*#__PURE__*/React.createElement("div", null, family.category ? family.category.label : "General"), /*#__PURE__*/React.createElement("span", null, "Fidelity"), /*#__PURE__*/React.createElement("div", null, fidelityChip(version.fidelity, version.aiGenerated) || /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, noCheckReason(version))))), /*#__PURE__*/React.createElement("div", {
      className: "iw-card"
    }, /*#__PURE__*/React.createElement("h6", null, "Then"), /*#__PURE__*/React.createElement("div", {
      className: "ec-inline iw-acts"
    }, /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy || !ticked.length || required && !approved,
      title: required && !approved ? "Disabled until approved" : "",
      onClick: () => this.run(false)
    }, busy === "hires" ? "Rendering hi-res…" : busy === "export" ? "Exporting…" : needsHires ? `Make hi-res and export ${ticked.length * exportLangs.length}` : `Export ${ticked.length * exportLangs.length}`), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy || !ticked.length,
      onClick: () => this.run(true)
    }, "Export with DRAFT mark"), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy || required && !approved,
      title: required && !approved ? "Disabled until approved" : this.props.shopify && this.props.shopify.connected ? `Writes to ${this.props.shopify.shop}` : "",
      onClick: () => this.setState({
        shopifyOpen: true
      })
    }, published ? "Push to Shopify again" : "Push to Shopify")), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, required && !approved ? "Disabled until approved. " : "", "\"Export with DRAFT mark\" gives a reviewer a real crop now.", published ? " This version is live on Shopify." : ""))))), shopifyOpen ? /*#__PURE__*/React.createElement(ShopifyPushDialog, {
      family: family,
      version: version,
      history: this.props.history,
      onClose: () => this.setState({
        shopifyOpen: false
      }),
      onPushed: r => {
        this.setState({
          shopifyOpen: false
        });
        this.props.onPushed(r);
      }
    }) : null);
  }
}
export const ImageWorkspaceViewComponent = withRouter(ImageWorkspace);
export default {
  ImageWorkspaceViewComponent
};